import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ServiceLamp } from "../components/ServiceLamp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { AuthImg, UploadButton } from "../components/AuthImg";
import { DocumentTile } from "../components/DocumentViewer";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Car as CarIcon, Plus, Wrench, FileText, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import AuditLine from "../components/AuditLine";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { fmtRoDate } from "../lib/dateFmt";
import { Checkbox } from "../components/ui/checkbox";
import { missingCarFields, missingCarDocs } from "./Autoturisme";
import { useConfirm } from "../components/ConfirmProvider";
import FuelByPersonReport from "../components/FuelByPersonReport";
import TalonRescanDialog from "./cars/TalonRescanDialog";
import CarPartsAndHistoryPane from "./cars/CarPartsAndHistoryPane";
import RepairOrServiceDialog from "./cars/RepairOrServiceDialog";
import { MaintenanceHistoryButton } from "../components/MaintenanceHistory";
import { ToolSetsHistorySection } from "../components/ToolSetsHistorySection";

export default function CarDetail() {
  const confirm = useConfirm();
  const { id } = useParams();
  const { canDo, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [obs, setObs] = useState([]);
  const [fuel, setFuel] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [persons, setPersons] = useState([]);
  const [repairOpen, setRepairOpen] = useState(false);
  const [defectOpen, setDefectOpen] = useState(false);
  const [defectReason, setDefectReason] = useState("");
  const [defectSeverity, setDefectSeverity] = useState("medium");
  const [defectDrivable, setDefectDrivable] = useState(false);
  const [defectSparePart, setDefectSparePart] = useState("");
  const [sparePartsCatalog, setSparePartsCatalog] = useState([]);
  const [editingDefect, setEditingDefect] = useState(null); // defect object pentru edit/repară
  const [defectImageIds, setDefectImageIds] = useState([]);
  const [defectVideoIds, setDefectVideoIds] = useState([]);
  const [previewImage, setPreviewImage] = useState(null); // { fileId, name }
  const [sellOpen, setSellOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | dirty | saving | saved | error
  const saveTimer = useRef(null);
  const skipNextAutoSave = useRef(true); // don't auto-save on initial load
  const lastLoadedJson = useRef("");
  const [tabValue, setTabValue] = useState("date");
  const [talonRescanOpen, setTalonRescanOpen] = useState(false);
  const [siblingIds, setSiblingIds] = useState([]);

  const load = async () => {
    const [a, b, f, sp, ps, allCars] = await Promise.all([
      api.get(`/cars/${id}`),
      api.get(`/cars/${id}/observations`),
      api.get(`/cars/${id}/fuel`),
      api.get(`/cars/${id}/spare-parts`).catch(() => ({ data: [] })),
      api.get(`/persons`).catch(() => ({ data: [] })),
      api.get(`/cars`).catch(() => ({ data: [] })),
    ]);
    skipNextAutoSave.current = true;
    setC(a.data);
    lastLoadedJson.current = JSON.stringify(a.data);
    setObs(b.data); setFuel(f.data); setSpareParts(sp.data || []);
    setPersons(ps.data || []);
    setSiblingIds((allCars.data || []).map((x) => x.id));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Prev/Next navigation
  const currentIdx = siblingIds.indexOf(id);
  const prevId = currentIdx > 0 ? siblingIds[currentIdx - 1] : null;
  const nextId = currentIdx >= 0 && currentIdx < siblingIds.length - 1 ? siblingIds[currentIdx + 1] : null;
  const goTo = (cid) => {
    if (!cid) return;
    const hash = (typeof window !== "undefined" ? window.location.hash : "") || "";
    navigate(`/autoturisme/${cid}${hash}`);
  };
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const editable = e.target?.isContentEditable;
      if (tag === "input" || tag === "textarea" || tag === "select" || editable) return;
      if (e.altKey || e.metaKey || e.ctrlKey) return;
      if (e.key === "ArrowLeft" && prevId) { e.preventDefault(); goTo(prevId); }
      else if (e.key === "ArrowRight" && nextId) { e.preventDefault(); goTo(nextId); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    /* eslint-disable-next-line */
  }, [prevId, nextId]);

  // Auto-save: debounced 1s after any change to `c`
  useEffect(() => {
    if (!c) return;
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }
    const currentJson = JSON.stringify(c);
    if (currentJson === lastLoadedJson.current) return;
    if (!canDo("AUTOTURISME", "edit")) return;
    setSaveStatus("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        const { data } = await api.put(`/cars/${id}`, c);
        lastLoadedJson.current = JSON.stringify(data);
        skipNextAutoSave.current = true;
        setC(data);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus((s) => (s === "saved" ? "idle" : s)), 2000);
      } catch {
        setSaveStatus("error");
      }
    }, 1000);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line
  }, [c]);

  useEffect(() => {
    if (c?.is_equipment) setTabValue((t) => (["combustibil", "fuel-report"].includes(t) ? t : "combustibil"));
  }, [c?.is_equipment]);

  if (!c) return <div className="text-zinc-500">Se încarcă...</div>;

  const setField = (k, v) => setC((x) => ({ ...x, [k]: v }));
  const canEdit = canDo("AUTOTURISME", "edit");
  const isEquipment = !!c.is_equipment;

  // Auto-compute helpers
  const addYears = (dateStr, years) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    d.setFullYear(d.getFullYear() + years);
    return d.toISOString().slice(0, 10);
  };
  const setMfkLast = (v) => {
    const expire = addYears(v, 2);
    setC((x) => ({ ...x, mfk_last: v, mfk_expire: expire || x.mfk_expire }));
  };
  const setRevizieLastKm = (v) => {
    const km = parseFloat(v || "0");
    setC((x) => ({ ...x, revizie_last_km: km, revizie_next_km: km > 0 ? km + 10000 : x.revizie_next_km }));
  };

  const remove = async () => {
    if (!(await confirm({ body: "Ștergi autoturismul?", danger: true }))) return;
    await api.delete(`/cars/${id}`);
    navigate("/autoturisme");
  };

  const today = new Date().toISOString().slice(0, 10);
  const mfkExpired = c.mfk_expire && c.mfk_expire <= today;
  const revizieDue = c.revizie_next_km && (c.kilometri || 0) >= c.revizie_next_km;
  const hasOrange = obs.length > 0;

  return (
    <div data-testid="page-car-detail">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900" data-testid="back-btn">
            <ArrowLeft size={16} /> Înapoi
          </button>
          <div className="h-5 w-px bg-zinc-200" />
          <div className="inline-flex items-center gap-1" data-testid="car-nav">
            <Button
              size="sm"
              variant="outline"
              disabled={!prevId}
              onClick={() => goTo(prevId)}
              className="h-8 px-2 disabled:opacity-40"
              title={prevId ? "Mașina anterioară (← săgeată stânga)" : "Ești la prima mașină"}
              data-testid="car-nav-prev"
            >
              <ChevronLeft size={16} />
              <span className="hidden md:inline ml-1 text-xs">Anterior</span>
            </Button>
            {currentIdx >= 0 && siblingIds.length > 0 && (
              <span className="text-[11px] text-zinc-500 px-2 font-mono" data-testid="car-nav-position">
                {currentIdx + 1} / {siblingIds.length}
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={!nextId}
              onClick={() => goTo(nextId)}
              className="h-8 px-2 disabled:opacity-40"
              title={nextId ? "Mașina următoare (→ săgeată dreapta)" : "Ești la ultima mașină"}
              data-testid="car-nav-next"
            >
              <span className="hidden md:inline mr-1 text-xs">Următor</span>
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isEquipment && (c.status === "sold" ? (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-100 text-zinc-700 font-bold text-sm" data-testid="sold-banner">
              🚫 VÂNDUTĂ {c.sold_at ? `· ${fmtRoDate(c.sold_at)}` : ""} {c.sale_price ? `· ${Number(c.sale_price).toLocaleString("ro-RO")} CHF` : ""}
            </span>
          ) : (
            <>
              {canEdit && c.status === "defect" && (
                <Button onClick={() => setRepairOpen(true)} className="bg-[var(--green-valid)] hover:opacity-90 text-white" data-testid="repair-btn">
                  <Wrench size={16} className="mr-1"/> REPARĂ
                </Button>
              )}
              {canEdit && (
                <Button variant="outline" onClick={() => {
                  setDefectReason(""); setDefectSeverity("medium"); setDefectDrivable(false); setDefectSparePart(""); setDefectImageIds([]); setDefectVideoIds([]);
                  // Load spare parts catalog
                  api.get(`/cars/spare-parts/catalog`).then((r) => setSparePartsCatalog(r.data?.items || [])).catch(() => setSparePartsCatalog([]));
                  setDefectOpen(true);
                }} className="border-[var(--red-bila)] text-[var(--red-bila)] hover:bg-[var(--red-bila-bg)]" data-testid="mark-defect-btn">
                  + Raportează defect
                </Button>
              )}
            </>
          ))}
          {isAdmin && !isEquipment && c.status !== "sold" && (
            <Button variant="outline" onClick={() => setSellOpen(true)} className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="sell-btn">
              💰 Vinde
            </Button>
          )}
          {isAdmin && c.status === "sold" && (
            <Button
              variant="outline"
              onClick={async () => {
                if (!(await confirm({ body: "Anulezi vânzarea? Mașina revine la status funcțional.", danger: true }))) return;
                await api.post(`/cars/${id}/unsell`);
                toast.success("Vânzare anulată");
                load();
              }}
              data-testid="unsell-btn"
            >
              ↺ Anulează vânzarea
            </Button>
          )}
          {canEdit && c.status !== "sold" && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium" data-testid="save-status">
              {saveStatus === "idle" && <span className="text-zinc-400">Salvare automată activă</span>}
              {saveStatus === "dirty" && <span className="text-amber-600">● Modificări nesalvate…</span>}
              {saveStatus === "saving" && <span className="text-[var(--primary)]">⟳ Se salvează…</span>}
              {saveStatus === "saved" && <span className="text-[var(--green-valid)]">✓ Salvat</span>}
              {saveStatus === "error" && <span className="text-red-600">⚠ Eroare salvare</span>}
            </span>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={async () => {
                const msg = isEquipment
                  ? "Transformi acest echipament în autoturism normal? Vor apărea toate tab-urile (MFK, documente, șoferi etc.)."
                  : "Marchezi această mașină ca ECHIPAMENT (canistră, generator)? Va păstra doar evidența de combustibil.";
                if (!(await confirm({ body: msg }))) return;
                await api.put(`/cars/${id}`, { is_equipment: !isEquipment });
                toast.success(isEquipment ? "Transformat în autoturism" : "Marcat ca echipament");
                load();
              }}
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="toggle-equipment-btn"
            >
              {isEquipment ? "🚗 Transformă în autoturism" : "🛠 Marchează ca echipament"}
            </Button>
          )}
          {isAdmin && <Button variant="destructive" onClick={remove}><Trash2 size={16} className="mr-1" />Șterge</Button>}
        </div>
      </div>

      {!isEquipment && (c.defects || []).filter((d) => !d.fixed).length > 0 && (
        <div className="card-flat p-4 mb-4 border-l-4 border-l-red-600">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold text-red-700 flex items-center gap-2">
              ⚠ Defecte raportate ({(c.defects || []).filter((d) => !d.fixed).length})
            </div>
          </div>
          <div className="space-y-1.5">
            {(c.defects || []).filter((d) => !d.fixed).map((d) => {
              const sevColor = d.severity === "high" ? "bg-red-600 text-white" : d.severity === "low" ? "bg-yellow-400 text-zinc-900" : "bg-orange-500 text-white";
              const sevLabel = d.severity === "high" ? "MARE" : d.severity === "low" ? "MICĂ" : "MEDIE";
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => canEdit && setEditingDefect(d)}
                  className={`w-full text-left flex items-start gap-2 text-sm bg-red-50 border border-red-100 rounded p-2 ${canEdit ? "hover:bg-red-100 transition-colors cursor-pointer" : "cursor-default"}`}
                  data-testid={`defect-row-${d.id}`}
                  title={canEdit ? "Click pentru a modifica sau marca rezolvat" : ""}
                >
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded shrink-0 ${sevColor}`}>{sevLabel}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{d.reason}</div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1">📥 <strong className="text-zinc-700">{d.reported_by || "—"}</strong> · {d.reported_at ? fmtRoDate(d.reported_at) : ""}</span>
                      {d.updated_by && d.updated_at && d.updated_at !== d.reported_at && (
                        <span className="inline-flex items-center gap-1 text-amber-700">✏️ <strong>{d.updated_by}</strong> · {fmtRoDate(d.updated_at)}</span>
                      )}
                      {d.drivable === true && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0 rounded bg-emerald-100 text-emerald-800 font-bold">circulă</span>
                      )}
                      {(d.image_ids || []).length > 0 && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0 rounded bg-blue-100 text-blue-800 font-bold">📷 {d.image_ids.length}</span>
                      )}
                      {(d.video_ids || []).length > 0 && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0 rounded bg-purple-100 text-purple-800 font-bold">🎬 {d.video_ids.length}</span>
                      )}
                    </div>
                    {(d.image_ids || []).length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {d.image_ids.map((fid, idx) => (
                          <span
                            key={fid}
                            role="button"
                            onClick={(e) => { e.stopPropagation(); setPreviewImage({ fileId: fid, name: `defect-${d.id}-${idx + 1}` }); }}
                            className="inline-block w-12 h-12 rounded overflow-hidden border border-blue-200 hover:border-blue-500 cursor-pointer bg-zinc-50"
                            data-testid={`defect-${d.id}-thumb-${idx}`}
                            title="Click pentru preview"
                          >
                            <AuthImg fileId={fid} className="w-full h-full object-cover" />
                          </span>
                        ))}
                      </div>
                    )}
                    {(d.video_ids || []).length > 0 && (
                      <div className="flex gap-1.5 mt-1.5 flex-wrap" data-testid={`defect-${d.id}-videos`}>
                        {d.video_ids.map((fid, idx) => (
                          <a
                            key={fid}
                            href={fileUrl(fid)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-purple-300 bg-purple-50 text-purple-800 text-[11px] font-bold hover:bg-purple-100"
                            data-testid={`defect-${d.id}-video-${idx}`}
                          >
                            🎬 Filmare #{idx + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <span
                      role="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!(await confirm({ body: "Ștergi acest defect raportat?", danger: true }))) return;
                        try {
                          await api.delete(`/cars/${id}/defects/${d.id}`);
                          toast.success("Defect șters");
                          load();
                        } catch (e) { toast.error("Eroare"); }
                      }}
                      className="text-zinc-400 hover:text-red-600 p-1 cursor-pointer"
                      title="Șterge defect"
                      data-testid={`del-defect-${d.id}`}
                    >
                      <Trash2 size={12}/>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(() => {
        if (isEquipment) return null;
        const missing = missingCarFields(c);
        const missingDocs = missingCarDocs(c);
        if (missing.length === 0 && missingDocs.length === 0) return null;
        const goTo = (tab) => {
          setTabValue(tab);
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="tab-${tab}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 50);
        };
        return (
          <div
            className="card-flat p-4 mb-4 bg-amber-50 border-amber-200 space-y-3"
            data-testid="car-missing-banner"
          >
            {missing.length > 0 && (
              <div className="flex items-start gap-3">
                <AlertCircle size={22} className="shrink-0 text-amber-600 mt-0.5"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-amber-900">
                    Date lipsă · Te rugăm să completezi {missing.length === 1 ? "câmpul" : `cele ${missing.length} câmpuri`} de mai jos
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {missing.map((m) => (
                      <span
                        key={m.key}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold border border-amber-200"
                        data-testid={`car-missing-${m.key}`}
                      >
                        {m.label}
                      </span>
                    ))}
                  </div>
                </div>
                {canEdit && (
                  <Button
                    size="sm"
                    onClick={() => goTo("date")}
                    className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    data-testid="car-missing-fill-btn"
                  >
                    Completează
                  </Button>
                )}
              </div>
            )}
            {missingDocs.length > 0 && (
              <div className={`flex items-start gap-3 ${missing.length > 0 ? "pt-3 border-t border-amber-200" : ""}`}>
                <AlertCircle size={22} className="shrink-0 text-amber-600 mt-0.5"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-amber-900">
                    Documente lipsă · Încarcă {missingDocs.length === 1 ? "documentul" : `cele ${missingDocs.length} documente`} obligatori{missingDocs.length === 1 ? "u" : "i"}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {missingDocs.map((m) => (
                      <span
                        key={m.key}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold border border-amber-200"
                        data-testid={`car-missing-doc-${m.key}`}
                      >
                        📎 {m.label}
                      </span>
                    ))}
                  </div>
                  <div className="text-[10px] text-amber-700 mt-1.5">
                    Contractul de vânzare-cumpărare este opțional.
                  </div>
                </div>
                {canEdit && (
                  <Button
                    size="sm"
                    onClick={() => goTo("documente")}
                    className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    data-testid="car-missing-docs-btn"
                  >
                    Încarcă
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })()}

      <div className="card-flat p-6 mb-6 flex items-start gap-6">
        <div className="flex flex-col items-center gap-2">
          <div className="relative">
            {c.logo_file_id ? (
              <AuthImg fileId={c.logo_file_id} className="w-24 h-24 rounded-md object-cover bg-zinc-50" />
            ) : (
              <div className="w-24 h-24 rounded-md bg-zinc-100 grid place-items-center text-zinc-400"><CarIcon size={36} /></div>
            )}
            {/* Bulină prioritar: MFK expirat=roșu pulsant; gap >20k=roșu; >15k=orange; >10k=galben */}
            {(() => {
              const lastKm = Number(c.revizie_last_km || 0);
              const gap = lastKm > 0 ? Number(c.kilometri || 0) - lastKm : 0;
              if (mfkExpired) return <span className="absolute -top-1 -right-1 bila-dot bila-red bila-pulse" title="MFK expirat" />;
              if (gap > 20000) return <span className="absolute -top-1 -right-1 bila-dot bila-red" title="Revizie >20.000 km" />;
              if (gap > 15000) return <span className="absolute -top-1 -right-1 bila-dot" style={{ background: "#F97316" }} title="Revizie >15.000 km" />;
              if (gap > 10000) return <span className="absolute -top-1 -right-1 bila-dot" style={{ background: "#EAB308" }} title="Revizie >10.000 km" />;
              if (hasOrange) return <span className="absolute -top-1 -right-1 bila-dot bila-orange" />;
              return null;
            })()}
          </div>
          {canEdit && (
            <UploadButton testId="upload-car-logo" label="Schimbă logo" onUploaded={(f) => { setField("logo_file_id", f.id); api.put(`/cars/${id}`, { logo_file_id: f.id }).then(load); }} />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-mono text-sm text-zinc-500">{c.numar_inmatriculare || "—"}</div>
            {c.intern_number && <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--primary-subtle)] text-[var(--primary)] font-bold" data-testid="car-intern-number">#{c.intern_number}</span>}
          </div>
          <h1 className="font-display text-3xl font-extrabold">
            {isEquipment ? (c.model || c.marca || "Echipament") : `${c.marca || "—"} ${c.model || ""}`}
            {isEquipment && <span className="ml-2 align-middle text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-bold border border-amber-300" data-testid="equipment-badge">🛠 ECHIPAMENT</span>}
          </h1>
          {!isEquipment && (
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-zinc-500">
            <span>Kilometraj: <strong className="text-zinc-900">{Math.round(c.kilometri || 0).toLocaleString()} km</strong></span>
            <span>·</span>
            <span>An: {c.an_fabricatie || "—"}</span>
          </div>
          )}
          <div className="flex items-center gap-2 mt-3">
            {mfkExpired && <span className="text-xs px-2 py-1 rounded-full bg-[var(--red-bila-bg)] text-[var(--red-bila)] font-semibold">MFK EXPIRAT</span>}
            {(() => {
              const lastKm = Number(c.revizie_last_km || 0);
              const gap = lastKm > 0 ? Number(c.kilometri || 0) - lastKm : 0;
              if (gap <= 10000) return null;
              let style, label;
              if (gap > 20000) { style = { background: "#FEE2E2", color: "#DC2626" }; label = "REVIZIE URGENTĂ"; }
              else if (gap > 15000) { style = { background: "#FFEDD5", color: "#EA580C" }; label = "REVIZIE SCADENTĂ"; }
              else { style = { background: "#FEF9C3", color: "#A16207" }; label = "REVIZIE PROGRAMARE"; }
              return (
                <span
                  className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-semibold"
                  style={style}
                  data-testid="car-service-overdue"
                >
                  <ServiceLamp gap={gap} size={16}/>
                  {label} · {Math.round(gap).toLocaleString("ro-RO")} km de la ultima revizie
                </span>
              );
            })()}
            {hasOrange && <span className="text-xs px-2 py-1 rounded-full bg-[var(--orange-bila-bg)] text-[var(--orange-bila)] font-semibold">{obs.length} OBSERVAȚII</span>}
          </div>
        </div>
      </div>

      {/* === SECȚIUNE PROMINENT: ULTIMELE REPARAȚII === */}
      {!isEquipment && <RecentRepairsCard carId={id} onSeeAll={() => setTabValue("ext-repairs")} />}

      {!isEquipment && (
        <div className="flex items-center gap-2">
          <MaintenanceHistoryButton carId={id} label="ISTORIC ÎNTREȚINERE" />
        </div>
      )}
      {!isEquipment && <ToolSetsHistorySection car_id={id} title="🧰 Seturi de scule transportate (istoric)" />}

      <Tabs value={tabValue} onValueChange={setTabValue}>
        <TabsList className="flex-wrap h-auto">
          {!isEquipment && (
            <>
          <TabsTrigger value="date" data-testid="tab-date">Date</TabsTrigger>
          <TabsTrigger value="mfk" data-testid="tab-mfk">MFK</TabsTrigger>
          <TabsTrigger value="revizie" data-testid="tab-revizie">Revizie</TabsTrigger>
          <TabsTrigger value="dpf" data-testid="tab-dpf">DPF Regen</TabsTrigger>
          <TabsTrigger value="ext-repairs" data-testid="tab-ext-repairs">📋 Istoric reparații și revizii</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="tab-drivers">Șoferi</TabsTrigger>
          <TabsTrigger value="proj-history" data-testid="tab-proj-history">Proiecte</TabsTrigger>
            </>
          )}
          <TabsTrigger value="fuel-report" data-testid="tab-fuel-report">📊 Litri/șofer</TabsTrigger>
          {!isEquipment && <TabsTrigger value="observatii" data-testid="tab-observatii">Observație</TabsTrigger>}
          <TabsTrigger value="combustibil" data-testid="tab-combustibil">Combustibil</TabsTrigger>
          {!isEquipment && (
            <>
          <TabsTrigger value="anvelope" data-testid="tab-anvelope">Anvelope</TabsTrigger>
          <TabsTrigger value="parcare" data-testid="tab-parcare">🅿️ Parcare</TabsTrigger>
          <TabsTrigger value="filtre" data-testid="tab-filtre">Filtre</TabsTrigger>
          <TabsTrigger value="piese" data-testid="tab-piese">🔧 Piese & Consumabile</TabsTrigger>
          <TabsTrigger value="documente" data-testid="tab-documente">Documente</TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="date">
          <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {canEdit && (
              <div className="md:col-span-2 flex items-center justify-between gap-3 p-3 rounded-lg border-2 border-cyan-300 bg-cyan-50/60">
                <div className="flex items-start gap-2">
                  <FileText size={18} className="text-cyan-700 mt-0.5"/>
                  <div>
                    <div className="text-sm font-bold text-cyan-900">Re-scanare talon cu AI</div>
                    <div className="text-[11px] text-zinc-600">
                      Încarcă o poză/PDF cu talonul. AI extrage Marca/Model/Fahrgestellnummer/Typenschein/Kontrollschild/MFK/Data cumpărării — alegi ce suprascrii.
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={() => setTalonRescanOpen(true)}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white whitespace-nowrap"
                  data-testid="open-talon-rescan"
                >
                  📷 Scanează talon
                </Button>
              </div>
            )}
            <F label="Număr înmatriculare" v={c.numar_inmatriculare} onChange={(v) => setField("numar_inmatriculare", v)} disabled={!canEdit} />
            <F label="Număr intern (firmă)" v={c.intern_number} onChange={(v) => setField("intern_number", v)} disabled={!canEdit} placeholder="ex: 1, 2, Auto 5" />
            <F label="Marcă" v={c.marca} onChange={(v) => setField("marca", v)} disabled={!canEdit} />
            <F label="Model" v={c.model} onChange={(v) => setField("model", v)} disabled={!canEdit} />
            <F label="Culoare" v={c.culoare} onChange={(v) => setField("culoare", v)} disabled={!canEdit} />
            <F label="An fabricație (câmp 36 — Baujahr)" type="number" v={c.an_fabricatie} onChange={(v) => setField("an_fabricatie", parseInt(v || "0"))} disabled={!canEdit} />
            <F label="Data achiziție (câmp 38 — Im Verkehr gesetzt am)" type="date" v={c.data_achizitie} onChange={(v) => setField("data_achizitie", v)} disabled={!canEdit} />
            <F label="Prima înmatriculare (câmp 1 — 1. Inverkehrsetzung)" type="date" v={c.prima_inmatriculare} onChange={(v) => setField("prima_inmatriculare", v)} disabled={!canEdit} />
            <F label="Preț achiziție" type="number" v={c.pret_achizitie} onChange={(v) => setField("pret_achizitie", parseFloat(v || "0"))} disabled={!canEdit} />
            <F label="Kilometri" type="number" v={c.kilometri} onChange={(v) => setField("kilometri", parseFloat(v || "0"))} disabled={!canEdit} />
            <F label="Număr locuri (inclusiv șofer)" type="number" v={c.nr_locuri} onChange={(v) => setField("nr_locuri", parseInt(v || "0"))} disabled={!canEdit} testId="car-nr-locuri" />
            <F label="Chassis Nr (VIN)" v={c.chassis_nr} onChange={(v) => setField("chassis_nr", v)} disabled={!canEdit} placeholder="ex: WBA…XXXXXXX" testId="car-chassis-nr" />
            <F label="Stamm Nr" v={c.stamm_nr} onChange={(v) => setField("stamm_nr", v)} disabled={!canEdit} placeholder="Stammnummer" testId="car-stamm-nr" />
            <F label="Typenschein (CH)" v={c.typenschein} onChange={(v) => setField("typenschein", v)} disabled={!canEdit} placeholder="ex: 1AB123" testId="car-typenschein" />
            <div className="md:col-span-2 mt-2 p-3 rounded-lg border-2 border-amber-300 bg-amber-50/50">
              <Label className="text-sm font-bold text-amber-800 flex items-center gap-1.5">
                ⚠ Note speciale (apar la alocare)
              </Label>
              <div className="text-[11px] text-zinc-500 mb-2">
                Particularități tehnice / instrucțiuni pentru șofer (ex: „Schimbă doar manual", „De verificat zilnic nivelul apei", „De verificat zilnic uleiul"). Apare ca avertisment în AssignDialog când mașina este selectată.
              </div>
              <textarea
                rows={3}
                disabled={!canEdit}
                value={c.special_notes || ""}
                onChange={(e) => setField("special_notes", e.target.value)}
                placeholder="ex: Schimbă doar manual&#10;De verificat zilnic nivelul apei&#10;Frână ușor sensibilă"
                className="w-full text-sm rounded-md border border-amber-300 px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:bg-zinc-50 disabled:cursor-not-allowed"
                data-testid="car-special-notes"
              />
            </div>

            {/* Tool set color — atribuie setul de scule pe culoare */}
            <div className="md:col-span-2 mt-4 p-4 rounded-lg border-2 border-zinc-200 bg-white" data-testid="car-tool-set-section">
              <Label className="text-sm font-bold uppercase tracking-wider mb-2 block">🎨 Set de scule (culoare)</Label>
              <div className="text-[11px] text-zinc-500 mb-3">
                Atribuie un set de scule (locație virtuală) acestei mașini. Sculele din locația „SET {`{`}culoare{`}`}" din Inventar vor fi afișate ca echipament al mașinii.
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { code: "", label: "—", emoji: "⬜", border: "border-zinc-300", bg: "bg-white" },
                  { code: "rosu", label: "Roșu", emoji: "🔴", border: "border-red-500", bg: "bg-red-50" },
                  { code: "galben", label: "Galben", emoji: "🟡", border: "border-yellow-500", bg: "bg-yellow-50" },
                  { code: "albastru", label: "Albastru", emoji: "🔵", border: "border-blue-500", bg: "bg-blue-50" },
                  { code: "verde", label: "Verde", emoji: "🟢", border: "border-emerald-500", bg: "bg-emerald-50" },
                  { code: "gri", label: "Gri", emoji: "⚪", border: "border-zinc-500", bg: "bg-zinc-100" },
                  { code: "mov", label: "Mov", emoji: "🟣", border: "border-purple-500", bg: "bg-purple-50" },
                ].map((opt) => {
                  const isSelected = (c.tool_set_color || "") === opt.code;
                  return (
                    <button
                      key={opt.code || "none"}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => setField("tool_set_color", opt.code || null)}
                      className={`px-3 py-2 rounded-md border-2 text-sm font-semibold transition-all ${
                        isSelected ? `${opt.border} ${opt.bg} ring-2 ring-offset-1 ring-zinc-400` : "border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-500"
                      } disabled:cursor-not-allowed disabled:opacity-50`}
                      data-testid={`car-tool-set-${opt.code || "none"}`}
                    >
                      <span className="mr-1.5">{opt.emoji}</span>{opt.label}
                    </button>
                  );
                })}
              </div>
              {c.tool_set_color && <CarToolSetView carId={c.id} color={c.tool_set_color}/>}
            </div>
            <div className="md:col-span-2 mt-4 p-4 rounded-lg border-2 border-dashed border-zinc-300 bg-zinc-50">
              <div className="flex items-center gap-3 mb-2">
                <Switch
                  data-testid="car-is-personal"
                  checked={!!c.is_personal}
                  onCheckedChange={(v) => setField("is_personal", v)}
                  disabled={!canEdit}
                />
                <Label className="text-sm font-bold uppercase tracking-wider">🚫 Mașină personală (exclusă din Planificator)</Label>
              </div>
              <div className="text-[11px] text-zinc-500 mb-3">
                Mașinile personale (sefu, admini, etc) nu apar în Planificator și nu sunt numărate la „auto disponibile". Rămân vizibile doar pe pagina Autoturisme cu eticheta corespunzătoare.
              </div>
              {c.is_personal && (
                <div>
                  <Label className="small-label">Atribuită lui</Label>
                  <Select
                    value={c.personal_owner_person_id || "__none__"}
                    onValueChange={(v) => setField("personal_owner_person_id", v === "__none__" ? null : v)}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="mt-1" data-testid="car-personal-owner">
                      <SelectValue placeholder="— Selectează persoana —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Nimeni —</SelectItem>
                      {[...persons]
                        .filter((p) => p.active !== false)
                        .sort((a, b) => `${a.nume} ${a.prenume}`.localeCompare(`${b.nume} ${b.prenume}`))
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nume} {p.prenume}{p.is_admin_user ? " (admin)" : ""}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="mfk">
          {/* Widget recurență MFK */}
          {(c.mfk_last || c.mfk_expire) && (() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const exp = c.mfk_expire ? new Date(c.mfk_expire) : null;
            const last = c.mfk_last ? new Date(c.mfk_last) : null;
            const daysRemaining = exp ? Math.ceil((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : null;
            const status = daysRemaining == null ? "unknown"
              : daysRemaining < 0 ? "expired"
              : daysRemaining <= 30 ? "soon"
              : daysRemaining <= 90 ? "warning"
              : "ok";
            const colors = {
              ok: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", icon: "🟢" },
              warning: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", icon: "🟡" },
              soon: { bg: "bg-orange-50", border: "border-orange-300", text: "text-orange-800", icon: "🟠" },
              expired: { bg: "bg-red-50", border: "border-red-300", text: "text-red-800", icon: "🔴" },
              unknown: { bg: "bg-zinc-50", border: "border-zinc-300", text: "text-zinc-700", icon: "⚪" },
            }[status];
            return (
              <div className={`card-flat ${colors.bg} ${colors.border} border-2 p-4 mb-4`} data-testid="mfk-recurrence-widget">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{colors.icon}</span>
                  <h3 className={`font-display font-bold text-base ${colors.text}`}>Recurență MFK</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Ultimul MFK</div>
                    <div className="font-bold text-zinc-900 mt-0.5">{last ? last.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Următorul (expiră)</div>
                    <div className="font-bold text-zinc-900 mt-0.5">{exp ? exp.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Rămase</div>
                    <div className={`font-extrabold text-xl ${colors.text} mt-0.5`} data-testid="mfk-days-remaining">
                      {daysRemaining == null ? "—" : daysRemaining < 0 ? `${Math.abs(daysRemaining)} zile depășite` : `${daysRemaining} zile`}
                    </div>
                  </div>
                </div>
                {status === "expired" && <div className={`mt-3 text-xs font-semibold ${colors.text}`}>⚠️ MFK EXPIRAT — nu mai poate circula legal. Programează urgent inspecție.</div>}
                {status === "soon" && <div className={`mt-3 text-xs font-semibold ${colors.text}`}>⏰ Expiră în mai puțin de 30 de zile — programează inspecția acum.</div>}
                {status === "warning" && <div className={`mt-3 text-xs ${colors.text}`}>⏳ Expiră în mai puțin de 3 luni — programează în următoarele săptămâni.</div>}
              </div>
            );
          })()}
          <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Data efectuării MFK" type="date" v={c.mfk_last} onChange={setMfkLast} disabled={!canEdit} />
            <F label="Data expirării MFK" type="date" v={c.mfk_expire} onChange={(v) => setField("mfk_expire", v)} disabled={!canEdit} />
            <div className="md:col-span-2 text-xs text-zinc-500">📅 Data expirării e auto-completată la +2 ani de la data efectuării. O poți modifica manual oricând.</div>
            {mfkExpired && <div className="md:col-span-2 p-3 rounded-md bg-[var(--red-bila-bg)] text-[var(--red-bila)] font-semibold">MFK expirat. Necesită atenție!</div>}
          </div>
        </TabsContent>

        <TabsContent value="revizie">
          {(() => {
            const missing = [];
            if (!c.oil_liters_per_service || Number(c.oil_liters_per_service) <= 0) missing.push("Litri ulei motor / schimb");
            if (!c.engine_oil_type) missing.push("Tip ulei motor (marcă/specificație)");
            if (!c.filter_oil_type || !c.filter_oil_dimensions) missing.push("Filtru ulei (tip + dimensiuni)");
            if (!c.filter_air_type || !c.filter_air_dimensions) missing.push("Filtru aer (tip + dimensiuni)");
            if (!c.filter_fuel_type || !c.filter_fuel_dimensions) missing.push("Filtru combustibil (tip + dimensiuni)");
            if (!c.filter_cabin_type || !c.filter_cabin_dimensions) missing.push("Filtru habitaclu (tip + dimensiuni)");
            if (missing.length === 0) return null;
            return (
              <div className="card-flat p-4 mb-3 border-l-4 border-amber-500 bg-amber-50" data-testid="revizie-missing-banner">
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none">⚠️</span>
                  <div className="flex-1">
                    <div className="font-bold text-amber-900">Date lipsă pentru revizie — completează urgent</div>
                    <div className="text-xs text-amber-800 mt-1">Adminul vede aceste avertismente pentru a comanda piesele corecte la timp:</div>
                    <ul className="mt-2 list-disc list-inside text-sm text-amber-900 space-y-0.5">
                      {missing.map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })()}
          <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Data ultimei revizii" type="date" v={c.revizie_last_date} onChange={(v) => setField("revizie_last_date", v)} disabled={!canEdit} />
            <F label="Km la ultima revizie" type="number" v={c.revizie_last_km} onChange={setRevizieLastKm} disabled={!canEdit} />
            <F label="Km scadent revizie următoare" type="number" v={c.revizie_next_km} onChange={(v) => setField("revizie_next_km", parseFloat(v || "0"))} disabled={!canEdit} />
            {revizieDue && <div className="md:col-span-2 p-3 rounded-md bg-[var(--red-bila-bg)] text-[var(--red-bila)] font-semibold">Revizie scadentă!</div>}
            {canEdit && <PerformServiceButton car={c} onDone={load} />}
            <div className="md:col-span-2 text-xs text-zinc-500">📅 Km-ul scadent următor se completează automat la +10.000 km de la km-ul ultimei revizii. Îl poți modifica manual oricând.</div>
            <div className="md:col-span-2 text-xs text-zinc-500">Km-ul ultimei revizii actualizează automat câmpul Kilometri al autoturismului dacă e mai mare.</div>
          </div>

          {/* Ulei motor + filtre — date pentru comandă piese revizie */}
          <div className="card-flat p-6 mt-3" data-testid="revizie-fluids-section">
            <h3 className="font-display text-base font-bold mb-3">🛢️ Ulei motor & filtre</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F
                label={<span>Litri ulei motor / schimb <span className="text-red-500">*</span></span>}
                type="number"
                step="0.1"
                v={c.oil_liters_per_service ?? ""}
                onChange={(v) => setField("oil_liters_per_service", parseFloat(v || "0"))}
                disabled={!canEdit}
                testId="field-oil-liters"
              />
              <F
                label={<span>Tip ulei motor <span className="text-red-500">*</span></span>}
                v={c.engine_oil_type || ""}
                onChange={(v) => setField("engine_oil_type", v)}
                disabled={!canEdit}
                placeholder="ex: 5W-30 Castrol Edge"
                testId="field-engine-oil-type"
              />
            </div>
            <div className="mt-5 space-y-3">
              {[
                { key: "filter_oil", icon: "🛢️", title: "Filtru ulei" },
                { key: "filter_air", icon: "💨", title: "Filtru aer" },
                { key: "filter_fuel", icon: "⛽", title: "Filtru combustibil" },
                { key: "filter_cabin", icon: "🧊", title: "Filtru habitaclu" },
              ].map((f) => (
                <div key={f.key} className="rounded-lg border border-zinc-200 p-3 bg-zinc-50/50" data-testid={`filter-row-${f.key}`}>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <span>{f.icon}</span> {f.title}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <F
                      label={<span>Tip / cod piesă <span className="text-red-500">*</span></span>}
                      v={c[`${f.key}_type`] || ""}
                      onChange={(v) => setField(`${f.key}_type`, v)}
                      disabled={!canEdit}
                      placeholder="ex: Mann WK 939/12x"
                      testId={`${f.key}-type`}
                    />
                    <F
                      label={<span>Dimensiuni <span className="text-red-500">*</span></span>}
                      v={c[`${f.key}_dimensions`] || ""}
                      onChange={(v) => setField(`${f.key}_dimensions`, v)}
                      disabled={!canEdit}
                      placeholder="ex: Ø78 mm · H 75 mm"
                      testId={`${f.key}-dimensions`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="dpf">
          <div className="card-flat p-6 space-y-4">
            <div className="flex items-center justify-between p-3 rounded-md bg-zinc-50 border border-zinc-200">
              <div>
                <div className="font-semibold text-sm">🚫 DPF anulat permanent</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {c.dpf_disabled
                    ? `Anulat ${c.dpf_disabled_at ? fmtRoDate(c.dpf_disabled_at) : ""}${c.dpf_disabled_by ? ` de ${c.dpf_disabled_by}` : ""}. Nu mai necesită regenerare.`
                    : "Marchează când DPF-ul a fost scos/anulat fizic — nu se va mai cere regenerare."}
                </div>
              </div>
              {canEdit && (
                <Button
                  variant={c.dpf_disabled ? "outline" : "default"}
                  size="sm"
                  className={c.dpf_disabled ? "" : "bg-zinc-900 hover:bg-zinc-700"}
                  data-testid="dpf-disable-toggle"
                  onClick={async () => {
                    try {
                      if (c.dpf_disabled) {
                        await api.post(`/cars/${id}/dpf-enable`);
                        toast.success("DPF reactivat");
                      } else {
                        if (!(await confirm({ body: "Marchezi DPF-ul ca anulat permanent? Nu va mai apărea avertismentul de regenerare.", danger: true }))) return;
                        await api.post(`/cars/${id}/dpf-disable`);
                        toast.success("DPF anulat");
                      }
                      load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
                  }}
                >
                  {c.dpf_disabled ? "Reactivează DPF" : "Anulează DPF"}
                </Button>
              )}
            </div>
            {!c.dpf_disabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Data ultimului DPF Regen" type="date" v={c.dpf_last_date} onChange={(v) => setField("dpf_last_date", v)} disabled={!canEdit} />
                <F label="Km la ultimul DPF Regen" type="number" v={c.dpf_last_km} onChange={(v) => setField("dpf_last_km", parseFloat(v || "0"))} disabled={!canEdit} />
                <F label="Data următorului DPF Regen" type="date" v={c.dpf_next_date} onChange={(v) => setField("dpf_next_date", v)} disabled={!canEdit} />
                <F label="Km următor DPF Regen" type="number" v={c.dpf_next_km} onChange={(v) => setField("dpf_next_km", parseFloat(v || "0"))} disabled={!canEdit} />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ext-repairs">
          <ExternalRepairsPane carId={id} canEdit={canEdit}/>
        </TabsContent>

        <TabsContent value="drivers">
          <DriverOnDatePane carId={id}/>
          <DailyDriverHistoryPane carId={id}/>
          <DriversHistoryPane carId={id}/>
        </TabsContent>

        <TabsContent value="proj-history">
          <ProjectsHistoryPane carId={id}/>
        </TabsContent>

        <TabsContent value="fuel-report">
          <FuelByPersonReport carId={id}/>
        </TabsContent>

        <TabsContent value="observatii">
          <ObservationsPane carId={id} obs={obs} onChanged={load} canEdit={canEdit} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="combustibil">
          <FuelPane carId={id} rows={fuel} onChanged={load} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="anvelope">
          <div className="card-flat p-6 space-y-4">
            <F label="Dimensiune anvelope" v={c.anvelope_dimensiune} onChange={(v) => setField("anvelope_dimensiune", v)} disabled={!canEdit} />
            <div className="flex items-center gap-2 pt-3 border-t border-[var(--border)]">
              <input
                id="tires_worn"
                type="checkbox"
                checked={!!c.tires_worn}
                onChange={(e) => setField("tires_worn", e.target.checked)}
                disabled={!canEdit}
                className="w-4 h-4 accent-red-600 cursor-pointer"
                data-testid="tires-worn-toggle"
              />
              <label htmlFor="tires_worn" className="text-sm font-semibold cursor-pointer flex items-center gap-1.5">
                <span className="text-base">🛞</span> Marchează cauciucurile ca <span className="text-red-600">UZATE</span>
              </label>
            </div>
            <div className="text-[11px] text-zinc-400">Va apărea un cauciuc roșu în dreptul mașinii în lista flotei.</div>
          </div>
        </TabsContent>

        <TabsContent value="parcare">
          <ParkingPermitsPane
            permits={c.parking_permits || []}
            canEdit={canEdit}
            onChange={(next) => setField("parking_permits", next)}
          />
        </TabsContent>

        <TabsContent value="filtre">
          <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Dimensiune filtru aer" v={c.filtru_aer} onChange={(v) => setField("filtru_aer", v)} disabled={!canEdit} />
            <F label="Dimensiune filtru motorină" v={c.filtru_motorina} onChange={(v) => setField("filtru_motorina", v)} disabled={!canEdit} />
            <F label="Dimensiune filtru ulei" v={c.filtru_ulei} onChange={(v) => setField("filtru_ulei", v)} disabled={!canEdit} />
            <div className="md:col-span-3 pt-3 border-t border-[var(--border)]">
              <Label className="small-label">Necesar ulei la service</Label>
              <Input type="number" step="0.1" value={c.oil_liters_per_service ?? 0} onChange={(e) => setField("oil_liters_per_service", parseFloat(e.target.value || "0"))} disabled={!canEdit} className="mt-2 w-40" />
              <span className="text-xs text-zinc-500 ml-2">litri/service (folosit la REPARĂ)</span>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="piese">
          <CarPartsAndHistoryPane car={c} canEdit={canEdit} onChanged={load} />
        </TabsContent>

        <TabsContent value="documente">
          <DocumentsPane carId={id} documente={c.documente || []} onChanged={(docs) => setField("documente", docs)} canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      {repairOpen && (
        <RepairOrServiceDialog
          car={c}
          mode="repair"
          defects={(c.defects || []).filter((d) => !d.fixed)}
          onClose={() => setRepairOpen(false)}
          onDone={() => { setRepairOpen(false); load(); }}
        />
      )}

      {/* Edit/Repair existing defect dialog */}
      {editingDefect && (
        <EditDefectDialog
          defect={editingDefect}
          carId={id}
          carLabel={`${c.marca || ""} ${c.model || ""} (${c.numar_inmatriculare || c.intern_number || "—"})`}
          onClose={() => setEditingDefect(null)}
          onPreview={setPreviewImage}
          onSaved={async (payload) => {
            try {
              await api.patch(`/cars/${id}/defects/${editingDefect.id}`, payload);
              toast.success(payload.fixed ? "✓ Defect marcat ca rezolvat" : "Defect modificat");
              setEditingDefect(null);
              load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Eroare la salvare");
            }
          }}
        />
      )}

      {/* Defect reason dialog */}
      <Dialog open={defectOpen} onOpenChange={setDefectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Raportează defect</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-600">
              {c.marca} {c.model} ({c.numar_inmatriculare || c.intern_number || "—"})
            </div>
            <div>
              <Label className="small-label">Descriere defect</Label>
              <Textarea
                data-testid="defect-reason"
                value={defectReason}
                onChange={(e) => setDefectReason(e.target.value)}
                rows={4}
                placeholder="ex: Bate roata din față, schimb plăcuțe, etc."
                autoFocus
              />
              <div className="text-[11px] text-zinc-400 mt-1">Mesajul va apărea în panoul de alerte din Dashboard.</div>
            </div>
            <div>
              <Label className="small-label">Gravitate</Label>
              <div className="flex gap-2 mt-1">
                {[
                  { v: "low", label: "Mică", color: "bg-yellow-400 hover:bg-yellow-500", text: "text-zinc-900" },
                  { v: "medium", label: "Medie", color: "bg-orange-500 hover:bg-orange-600", text: "text-white" },
                  { v: "high", label: "Mare", color: "bg-red-600 hover:bg-red-700", text: "text-white" },
                ].map((s) => (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => setDefectSeverity(s.v)}
                    className={`flex-1 rounded-md py-2 text-xs font-bold transition-all ${s.color} ${s.text} ${defectSeverity === s.v ? "ring-2 ring-offset-2 ring-zinc-900 scale-105" : "opacity-70 hover:opacity-100"}`}
                    data-testid={`severity-${s.v}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="small-label">Mai poate fi condusă?</Label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setDefectDrivable(true)}
                  className={`flex-1 rounded-md py-2 text-xs font-bold transition-all border-2 ${defectDrivable ? "bg-emerald-100 border-emerald-600 text-emerald-900 ring-2 ring-emerald-400" : "bg-white border-zinc-200 text-zinc-500 hover:border-emerald-300"}`}
                  data-testid="drivable-yes"
                >
                  ✓ DA — poate circula
                </button>
                <button
                  type="button"
                  onClick={() => setDefectDrivable(false)}
                  className={`flex-1 rounded-md py-2 text-xs font-bold transition-all border-2 ${!defectDrivable ? "bg-red-100 border-red-600 text-red-900 ring-2 ring-red-400" : "bg-white border-zinc-200 text-zinc-500 hover:border-red-300"}`}
                  data-testid="drivable-no"
                >
                  ✗ NU — imobilizată
                </button>
              </div>
              <div className="text-[11px] text-zinc-400 mt-1">
                {defectDrivable
                  ? "Mașina rămâne disponibilă pentru alocări în Planificator (cu etichetă defect)."
                  : "Mașina nu va apărea ca disponibilă până la reparare."}
              </div>
            </div>
            <div className="border-t border-zinc-200 pt-3">
              <Label className="small-label">🔩 Piesă de schimb necesară (opțional)</Label>
              <Input
                list="spare-parts-catalog"
                value={defectSparePart}
                onChange={(e) => setDefectSparePart(e.target.value)}
                placeholder="ex: Plăcuțe frână față, Filtru ulei…"
                data-testid="defect-spare-part"
                className="mt-1"
              />
              <datalist id="spare-parts-catalog">
                {sparePartsCatalog.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.in_stock > 0 ? `${s.name} (în stoc: ${s.in_stock})` : `${s.name} (lipsă)`}
                  </option>
                ))}
              </datalist>
              {defectSparePart.trim() && (() => {
                const match = sparePartsCatalog.find((s) => (s.name || "").toLowerCase() === defectSparePart.trim().toLowerCase());
                if (match && match.in_stock > 0) {
                  return <div className="text-[11px] text-emerald-700 font-semibold mt-1 bg-emerald-50 px-2 py-1 rounded">✓ {match.in_stock} bucăți în stoc — folosim direct</div>;
                } else if (match) {
                  return <div className="text-[11px] text-amber-700 font-semibold mt-1 bg-amber-50 px-2 py-1 rounded">⚠ Piesă cunoscută dar lipsă — banner roșu pentru admin</div>;
                } else {
                  return <div className="text-[11px] text-blue-700 font-semibold mt-1 bg-blue-50 px-2 py-1 rounded">+ Piesă nouă — se va adăuga rubrică în inventar</div>;
                }
              })()}
            </div>
            <div className="border-t border-zinc-200 pt-3">
              <Label className="small-label">📷 Poze defect (opțional)</Label>
              <div className="flex items-center gap-2 flex-wrap mt-2">
                <UploadButton
                  testId="defect-image-upload"
                  label={defectImageIds.length > 0 ? `+ Mai adaugă (${defectImageIds.length})` : "📷 Adaugă poză"}
                  accept="image/*"
                  onUploaded={(f) => setDefectImageIds((prev) => [...prev, f.id])}
                />
                {defectImageIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setDefectImageIds([])}
                    className="text-[11px] text-red-600 hover:underline"
                    data-testid="defect-images-clear"
                  >
                    🗑 Șterge toate
                  </button>
                )}
              </div>
              {defectImageIds.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2" data-testid="defect-images-preview">
                  {defectImageIds.map((fid, idx) => (
                    <div key={fid} className="relative group rounded overflow-hidden border border-zinc-200 aspect-square bg-zinc-50">
                      <button
                        type="button"
                        onClick={() => setPreviewImage({ fileId: fid, name: `defect-${idx + 1}` })}
                        className="w-full h-full block"
                        title="Click pentru preview"
                      >
                        <AuthImg fileId={fid} className="w-full h-full object-cover" alt={`Poză defect ${idx + 1}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDefectImageIds((prev) => prev.filter((x) => x !== fid))}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Șterge"
                        data-testid={`defect-img-remove-${idx}`}
                      >
                        ✗
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t pt-3">
              <Label className="text-xs">🎬 Filmare defect <span className="text-zinc-400 font-normal">(opțional · mp4, mov)</span></Label>
              <div className="flex items-center gap-2 flex-wrap mt-1.5">
                <UploadButton
                  testId="defect-video-upload"
                  label={defectVideoIds.length > 0 ? `+ Mai adaugă filmare (${defectVideoIds.length})` : "🎬 Adaugă filmare"}
                  accept="video/*"
                  onUploaded={(f) => setDefectVideoIds((prev) => [...prev, f.id])}
                />
                {defectVideoIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setDefectVideoIds([])}
                    className="text-[11px] text-red-600 hover:underline"
                    data-testid="defect-videos-clear"
                  >
                    🗑 Șterge toate
                  </button>
                )}
              </div>
              {defectVideoIds.length > 0 && (
                <ul className="space-y-1.5 mt-2" data-testid="defect-videos-preview">
                  {defectVideoIds.map((fid, idx) => (
                    <li key={fid} className="flex items-center justify-between gap-2 p-2 rounded border border-zinc-200 bg-zinc-50">
                      <div className="flex items-center gap-2 text-xs text-zinc-700 min-w-0 flex-1">
                        <span className="text-base">🎬</span>
                        <span className="truncate">Filmare #{idx + 1}</span>
                        <span className="text-[10px] text-emerald-700 font-bold uppercase">Atașată</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDefectVideoIds((prev) => prev.filter((x) => x !== fid))}
                        className="text-[11px] text-red-600 hover:text-red-800"
                        title="Șterge filmarea"
                        data-testid={`defect-video-remove-${idx}`}
                      >
                        ✗ Șterge
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDefectOpen(false)}>Anulează</Button>
            <Button
              onClick={async () => {
                if (!defectReason.trim()) { toast.error("Introdu descrierea defectului"); return; }
                try {
                  await api.post(`/cars/${id}/defects`, { reason: defectReason.trim(), severity: defectSeverity, drivable: defectDrivable, spare_part_name: defectSparePart.trim() || null, image_ids: defectImageIds, video_ids: defectVideoIds });
                  toast.success("Defect raportat");
                  setDefectOpen(false);
                  load();
                } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
              }}
              className="bg-[var(--red-bila)] hover:opacity-90 text-white"
              data-testid="confirm-defect"
            >
              Raportează defect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sell dialog */}
      <SellDialog open={sellOpen} car={c} onClose={() => setSellOpen(false)} onSold={load}/>

      {/* Image preview overlay (click pe thumbnail) */}
      {previewImage && (
        <Dialog open onOpenChange={() => setPreviewImage(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>📷 Preview poză defect</DialogTitle>
            </DialogHeader>
            <div className="flex justify-center bg-zinc-50 rounded p-2 max-h-[70vh] overflow-auto">
              <AuthImg fileId={previewImage.fileId} className="max-w-full max-h-[65vh] object-contain rounded" alt="Preview defect"/>
            </div>
            <DialogFooter>
              <a
                href={fileUrl(previewImage.fileId, { download: 1 })}
                download={`${previewImage.name || "defect"}.jpg`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-md text-sm font-semibold hover:opacity-90"
                data-testid="defect-image-download"
              >
                ⬇ Descarcă
              </a>
              <Button variant="outline" onClick={() => setPreviewImage(null)}>Închide</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <TalonRescanDialog
        open={talonRescanOpen}
        car={c}
        onClose={() => setTalonRescanOpen(false)}
        onApplied={async () => {
          // reîncarcă datele mașinii pentru a reflecta noile câmpuri
          try {
            const r = await api.get(`/cars/${id}`);
            setC(r.data);
            lastLoadedJson.current = JSON.stringify(r.data);
            skipNextAutoSave.current = true;
          } catch { /* ignore */ }
        }}
      />
    </div>
  );
}

function SparePartsList({ spareParts }) {
  if (spareParts.length === 0) {
    return (
      <div className="card-flat p-12 text-center">
        <div className="text-zinc-300"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mx-auto"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/></svg></div>
        <div className="font-display text-lg font-bold mt-4">Nicio piesă pe stoc</div>
        <p className="text-sm text-zinc-500 mt-2">Adaugă piese auto din modulul Inventar și asociază-le cu această mașină.</p>
      </div>
    );
  }
  return (
    <div className="card-flat overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50">
          <tr className="text-left">
            <th className="p-3">Piesă</th>
            <th className="p-3 text-right">Cantitate</th>
            <th className="p-3">Locație</th>
            <th className="p-3">Raft</th>
            <th className="p-3">Garanție</th>
          </tr>
        </thead>
        <tbody>
          {spareParts.map((p) => (
            <tr key={p.id} className="border-t border-[var(--border)]">
              <td className="p-3 font-medium">{p.name}</td>
              <td className="p-3 text-right">{p.quantity || 1}{p.unit ? ` ${p.unit}` : ""}</td>
              <td className="p-3 text-xs">{p.location || "—"}</td>
              <td className="p-3 text-xs font-mono">{p.shelf_row && p.shelf_col ? `${p.shelf_row}${p.shelf_col}` : "—"}</td>
              <td className="p-3 text-xs">{p.warranty_until || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RepairDialog({ car, spareParts, onClose, onDone }) {
  const [selected, setSelected] = useState(new Set());
  const [oilLiters, setOilLiters] = useState(car.oil_liters_per_service || 0);
  const [note, setNote] = useState("");
  const [oilStock, setOilStock] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    api.get("/inventory/oil-stock").then((r) => setOilStock(r.data?.total_liters || 0)).catch(() => {});
  }, []);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/cars/${car.id}/repair`, {
        spare_part_ids: [...selected],
        oil_liters: parseFloat(oilLiters || 0),
        note,
      });
      if (data?.oil_shortage > 0) {
        toast.warning(`Reparație înregistrată — atenție: lipsă ${data.oil_shortage} L ulei din stoc!`);
      } else {
        toast.success("Reparație înregistrată");
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally {
      setSubmitting(false);
    }
  };

  const oilOk = parseFloat(oilLiters || 0) <= oilStock;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>REPARĂ: {car.marca} {car.model} ({car.numar_inmatriculare})</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="card-flat p-3 bg-[var(--green-bg)] text-sm">
            <Wrench size={14} className="inline mr-1.5"/>După înregistrarea reparației, piesele selectate vor fi <strong>scoase din stoc</strong> (cantitate −1) și mașina revine la status <strong className="text-[var(--green-valid)]">funcțional</strong>.
          </div>

          <div>
            <Label className="small-label">Piese folosite ({selected.size} selectate)</Label>
            {spareParts.length === 0 ? (
              <div className="text-sm text-zinc-500 italic mt-2">Nicio piesă auto asociată cu această mașină este disponibilă pe stoc.</div>
            ) : (
              <div className="mt-2 max-h-60 overflow-y-auto border border-[var(--border)] rounded-md divide-y divide-[var(--border)]">
                {spareParts.map((p) => (
                  <label key={p.id} className="flex items-center gap-3 p-2.5 hover:bg-zinc-50 cursor-pointer">
                    <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`repair-part-${p.id}`} />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-xs text-zinc-500">
                        Stoc: {p.quantity || 1}{p.unit ? ` ${p.unit}` : ""}
                        {p.location ? ` · ${p.location}` : ""}
                        {p.shelf_row && p.shelf_col ? ` · ${p.shelf_row}${p.shelf_col}` : ""}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="small-label">Ulei consumat (L)</Label>
              <Input type="number" step="0.1" value={oilLiters} onChange={(e) => setOilLiters(e.target.value)} className="mt-2" data-testid="repair-oil" />
              <div className={`text-[11px] mt-1 ${oilOk ? "text-zinc-500" : "text-[var(--red-bila)] font-semibold"}`}>
                Stoc disponibil: {oilStock.toFixed(2)} L {!oilOk && "— stoc insuficient!"}
              </div>
            </div>
            <div>
              <Label className="small-label">Notă reparație</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-2" placeholder="ex: schimbat pompă apă" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[var(--green-valid)] hover:opacity-90 text-white" data-testid="submit-repair">
            <Wrench size={14} className="mr-1.5"/>{submitting ? "Se înregistrează..." : "Înregistrează reparația"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PerformServiceButton({ car, onDone }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const oilNeeded = Number(car?.oil_liters_per_service || 0);
  return (
    <div className="md:col-span-2 mt-2 flex flex-col gap-2">
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="perform-service-btn"
        >
          🔧 Marchează revizia efectuată (selectează piese)
        </Button>
        {oilNeeded > 0 && (
          <div className="text-xs text-zinc-600">
            Necesar ulei standard: <strong>{oilNeeded} L</strong>
          </div>
        )}
      </div>
      <div className="text-[11px] text-zinc-500">
        Se va deschide un dialog unde selectezi <strong>exact</strong> piesele consumate (ulei + filtre + alte piese) — fiecare scade din stoc cu cantitatea aleasă.
      </div>
      {dialogOpen && (
        <RepairOrServiceDialog
          car={car}
          mode="service"
          onClose={() => setDialogOpen(false)}
          onDone={() => { setDialogOpen(false); onDone && onDone(); }}
        />
      )}
    </div>
  );
}

function F({ label, v, onChange, type = "text", disabled, placeholder = "", testId, step }) {
  return (
    <div>
      <Label className="small-label">{label}</Label>
      <Input type={type} step={step} value={v ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder={placeholder} className="mt-2" data-testid={testId} />
    </div>
  );
}

function ObservationsPane({ carId, obs, onChanged, canEdit, isAdmin }) {
  const [text, setText] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const add = async () => {
    if (!text.trim()) return;
    await api.post(`/cars/${carId}/observations`, { text, date });
    setText("");
    onChanged();
    toast.success("Observație adăugată");
  };
  const remove = async (oid) => {
    await api.delete(`/cars/${carId}/observations/${oid}`);
    onChanged();
  };
  return (
    <div className="card-flat p-6">
      {canEdit && (
        <div className="flex gap-2 mb-4 items-end">
          <div><Label className="small-label">Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-2" /></div>
          <div className="flex-1"><Label className="small-label">Observație</Label><Input data-testid="obs-text" value={text} onChange={(e) => setText(e.target.value)} className="mt-2" /></div>
          <Button data-testid="obs-add" onClick={add} className="bg-[var(--primary)]"><Plus size={16}/> Adaugă</Button>
        </div>
      )}
      <div className="divide-y divide-[var(--border)]">
        {obs.map((o) => (
          <div key={o.id} className="py-3 flex items-start justify-between">
            <div>
              <div className="text-xs text-zinc-500">{fmtRoDate(o.date)}</div>
              <div className="text-sm">{o.text}</div>
              <AuditLine entry={o} testid={`obs-audit-${o.id}`} compact />
            </div>
            {isAdmin && <button onClick={() => remove(o.id)} className="text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 size={14}/></button>}
          </div>
        ))}
        {obs.length === 0 && <div className="text-sm text-zinc-500">Nicio observație.</div>}
      </div>
    </div>
  );
}

function FuelPane({ carId, rows, onChanged, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [persons, setPersons] = useState([]);
  const [form, setForm] = useState({
    data_alimentare: new Date().toISOString().slice(0, 10),
    numar_chip: "",
    litri: "",
    numar_km: "",
    persoana: "",
  });

  useEffect(() => {
    if (!open) return;
    api.get("/persons", { params: { active: true } })
      .then((r) => setPersons(Array.isArray(r.data) ? r.data : (r.data?.persons || [])))
      .catch(() => setPersons([]));
  }, [open]);

  const personNames = useMemo(() => {
    return persons
      .map((p) => p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "ro"));
  }, [persons]);

  const submit = async () => {
    try {
      await api.post(`/cars/${carId}/fuel`, {
        ...form,
        litri: parseFloat(form.litri || "0"),
        numar_km: parseFloat(form.numar_km || "0"),
      });
      setOpen(false);
      setForm({ data_alimentare: new Date().toISOString().slice(0, 10), numar_chip: "", litri: "", numar_km: "", persoana: "" });
      onChanged();
      toast.success("Alimentare înregistrată");
    } catch (e) { toast.error("Eroare"); }
  };

  const remove = async (fid) => {
    await api.delete(`/cars/${carId}/fuel/${fid}`);
    onChanged();
  };

  const avgConsum = useMemo(() => {
    const withC = rows.filter((r) => r.consum);
    if (withC.length === 0) return null;
    return (withC.reduce((s, r) => s + r.consum, 0) / withC.length).toFixed(2);
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="card-flat p-5 flex items-center justify-between">
        <div>
          <div className="small-label">Consum mediu</div>
          <div className="font-display text-3xl font-extrabold mt-1">{avgConsum ? `${avgConsum} L/100km` : "—"}</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-fuel-btn" className="bg-[var(--primary)]"><Plus size={16}/> Alimentare</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Alimentare combustibil</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data alimentare</Label><Input type="date" data-testid="fuel-date" value={form.data_alimentare} onChange={(e) => setForm({ ...form, data_alimentare: e.target.value })} /></div>
              <div><Label>Număr chip</Label><Input value={form.numar_chip} onChange={(e) => setForm({ ...form, numar_chip: e.target.value })} /></div>
              <div><Label>Litri</Label><Input type="number" step="0.01" data-testid="fuel-litri" value={form.litri} onChange={(e) => setForm({ ...form, litri: e.target.value })} /></div>
              <div><Label>Număr KM</Label><Input type="number" data-testid="fuel-km" value={form.numar_km} onChange={(e) => setForm({ ...form, numar_km: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Persoana care a alimentat</Label>
                <Input
                  list="fuel-person-options"
                  value={form.persoana}
                  onChange={(e) => setForm({ ...form, persoana: e.target.value })}
                  placeholder="Alege din listă sau scrie numele..."
                  data-testid="fuel-persoana"
                  autoComplete="off"
                />
                <datalist id="fuel-person-options">
                  {personNames.map((n) => (
                    <option key={n} value={n} />
                  ))}
                </datalist>
                <div className="text-[10px] text-zinc-500 mt-1">
                  💡 Scrie minimum 2 caractere ca să vezi sugestiile. Poți alege un angajat din listă sau introdu manual numele.
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
              <Button data-testid="submit-fuel" onClick={submit}>Salvează</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="p-3">Data</th>
              <th className="p-3">Chip</th>
              <th className="p-3 text-right">Litri</th>
              <th className="p-3 text-right">Km</th>
              <th className="p-3 text-right">Consum (L/100km)</th>
              <th className="p-3">Persoană</th>
              {isAdmin && <th className="p-3"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--border)] row-hover">
                <td className="p-3">{r.data_alimentare}</td>
                <td className="p-3 font-mono text-xs">{r.numar_chip || "—"}</td>
                <td className="p-3 text-right">{r.litri}</td>
                <td className="p-3 text-right">{r.numar_km}</td>
                <td className="p-3 text-right font-semibold">{r.consum ?? "—"}</td>
                <td className="p-3">{r.persoana || "—"}</td>
                {isAdmin && (
                  <td className="p-3 text-right">
                    <button onClick={() => remove(r.id)} className="text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 size={14}/></button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="p-6 text-center text-zinc-500">Fără alimentări.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const CAR_DOC_TYPES = ["Talon", "Poliță asigurare", "Contract vânzare-cumpărare", "Carte de identitate", "Leasing", "Service / Revizie", "MFK", "Vignetă", "Alte documente"];

function DocumentsPane({ carId, documente, onChanged, canEdit }) {
  const confirm = useConfirm();
  const [pendingFile, setPendingFile] = useState(null);
  const [pickedType, setPickedType] = useState(CAR_DOC_TYPES[0]);
  const [customType, setCustomType] = useState("");

  const onUploaded = (file) => {
    // Deschidem dialog de selectare tip
    setPendingFile(file);
    setPickedType(CAR_DOC_TYPES[0]);
    setCustomType("");
  };
  const confirmType = () => {
    if (!pendingFile) return;
    const finalType = pickedType === "Alte documente" && customType.trim()
      ? customType.trim()
      : pickedType;
    const next = [...(documente || []), {
      file_id: pendingFile.id,
      name: pendingFile.filename || pendingFile.name || "document",
      doc_type: finalType,
      uploaded_at: new Date().toISOString(),
    }];
    onChanged(next);
    toast.success(`${finalType} atașat`);
    setPendingFile(null);
  };
  const remove = async (idx) => {
    if (!(await confirm({ body: "Ștergi acest document?", danger: true }))) return;
    const next = (documente || []).filter((_, i) => i !== idx);
    onChanged(next);
    toast.success("Șters");
  };
  const updateType = (idx, newType) => {
    const next = [...(documente || [])];
    next[idx] = { ...next[idx], doc_type: newType };
    onChanged(next);
  };

  return (
    <div className="card-flat p-6 space-y-4" data-testid="documents-pane">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-base font-bold">Documente / Acte mașină</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Atașează poze cu actele mașinii sau fișiere PDF — fiecare cu titlu (asigurare, talon, etc.).</p>
        </div>
        {canEdit && (
          <UploadButton
            onUploaded={onUploaded}
            label="📎 Atașează document"
            accept="image/*,application/pdf"
            testId="upload-car-doc"
          />
        )}
      </div>

      {(!documente || documente.length === 0) ? (
        <div className="text-center text-sm text-zinc-400 italic py-8">Niciun document atașat încă.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {documente.map((d, idx) => (
            <div key={`${d.file_id}-${idx}`} className="relative" data-testid={`car-doc-${idx}`}>
              {/* Tipul documentului (titlu) — afișat sus, editabil */}
              <div className="mb-1.5">
                {canEdit ? (
                  <select
                    value={CAR_DOC_TYPES.includes(d.doc_type) ? d.doc_type : "Alte documente"}
                    onChange={(e) => {
                      if (e.target.value === "Alte documente") {
                        const v = window.prompt("Titlu document:", d.doc_type && !CAR_DOC_TYPES.includes(d.doc_type) ? d.doc_type : "");
                        if (v && v.trim()) updateType(idx, v.trim());
                      } else {
                        updateType(idx, e.target.value);
                      }
                    }}
                    className="text-[11px] font-bold text-[var(--primary)] bg-[var(--primary-subtle)] px-2 py-0.5 rounded w-full border-0 cursor-pointer"
                    data-testid={`doc-type-${idx}`}
                  >
                    {CAR_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    {d.doc_type && !CAR_DOC_TYPES.includes(d.doc_type) && (
                      <option value={d.doc_type}>{d.doc_type}</option>
                    )}
                  </select>
                ) : (
                  <div className="text-[11px] font-bold text-[var(--primary)] bg-[var(--primary-subtle)] px-2 py-0.5 rounded text-center truncate" title={d.doc_type || "Document"}>
                    {d.doc_type || "Document"}
                  </div>
                )}
              </div>
              <DocumentTile fileId={d.file_id} name={d.name} />
              <div className="px-2 pt-1 text-[10px] text-zinc-400 truncate">
                {d.uploaded_at ? fmtRoDate(d.uploaded_at) : ""}
              </div>
              {canEdit && (
                <button
                  onClick={() => remove(idx)}
                  className="absolute bottom-1.5 left-1.5 bg-white p-1.5 rounded-full shadow-md border border-red-200 z-10 hover:bg-red-50 hover:border-red-400"
                  title="Șterge documentul"
                  data-testid={`car-doc-remove-${idx}`}
                >
                  <Trash2 size={13} className="text-red-600"/>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialog selectare tip document după upload */}
      {pendingFile && (
        <Dialog open onOpenChange={(v) => !v && setPendingFile(null)}>
          <DialogContent className="max-w-sm" data-testid="doc-type-dialog">
            <DialogHeader>
              <DialogTitle>Tip document</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-zinc-600">Ce reprezintă acest fișier? <span className="font-mono text-xs text-zinc-400">({pendingFile.filename || "fișier"})</span></p>
              <div className="grid grid-cols-2 gap-2">
                {CAR_DOC_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setPickedType(t)}
                    className={`text-xs px-3 py-2 rounded-md border transition-colors ${pickedType === t ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`}
                    data-testid={`doc-type-opt-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  >{t}</button>
                ))}
              </div>
              {pickedType === "Alte documente" && (
                <Input
                  placeholder="Specifică tipul (ex: Cazier auto)"
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value)}
                  autoFocus
                  data-testid="doc-type-custom"
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingFile(null)}>Anulează</Button>
              <Button onClick={confirmType} className="bg-[var(--primary)]" data-testid="doc-type-confirm">Salvează</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SellDialog({ open, car, onClose, onSold }) {
  const [salePrice, setSalePrice] = useState("");
  const [soldAt, setSoldAt] = useState(new Date().toISOString().slice(0, 10));
  const [soldTo, setSoldTo] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setSalePrice(""); setSoldAt(new Date().toISOString().slice(0, 10)); setSoldTo(""); setNote(""); }
  }, [open]);

  if (!open || !car) return null;

  const submit = async () => {
    if (!salePrice || parseFloat(salePrice) <= 0) return toast.error("Specifică prețul de vânzare");
    setSubmitting(true);
    try {
      await api.post(`/cars/${car.id}/sell`, {
        sale_price: parseFloat(salePrice),
        sold_at: soldAt,
        sold_to: soldTo || null,
        sale_note: note || null,
      });
      toast.success("Mașină marcată ca vândută");
      onSold();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="sell-dialog">
        <DialogHeader><DialogTitle>💰 Vinde {car.marca} {car.model} ({car.numar_inmatriculare})</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="small-label">Preț vânzare (CHF) *</Label>
            <Input type="number" step="0.01" min={0} value={salePrice} onChange={(e) => setSalePrice(e.target.value)} className="mt-1" autoFocus data-testid="sell-price"/>
          </div>
          <div>
            <Label className="small-label">Data vânzării</Label>
            <Input type="date" value={soldAt} onChange={(e) => setSoldAt(e.target.value)} className="mt-1" data-testid="sell-date"/>
          </div>
          <div>
            <Label className="small-label">Cumpărător (opțional)</Label>
            <Input value={soldTo} onChange={(e) => setSoldTo(e.target.value)} className="mt-1" placeholder="ex: Garage XYZ, Persoană privată" data-testid="sell-buyer"/>
          </div>
          <div>
            <Label className="small-label">Notă (opțional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sell-submit">
            {submitting ? "Se vinde…" : "Marchează vândută"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExternalRepairsPane({ carId, canEdit }) {
  const confirm = useConfirm();
  const [externalRows, setExternalRows] = useState([]); // raw external_repairs (used by chart + add/delete)
  const [unified, setUnified] = useState([]); // toate: intern + extern
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), service_name: "", cost: "", currency: "CHF", description: "" });
  const [filter, setFilter] = useState("all"); // all | external | internal_repair | internal_service

  const load = async () => {
    try {
      const [extR, allR] = await Promise.all([
        api.get(`/cars/${carId}/external-repairs`).catch(() => ({ data: [] })),
        api.get(`/cars/${carId}/repairs-history`).catch(() => ({ data: [] })),
      ]);
      setExternalRows(extR.data || []);
      setUnified(allR.data || []);
    } catch { setExternalRows([]); setUnified([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [carId]);

  const totalExternalCost = externalRows.reduce((s, r) => s + (parseFloat(r.cost) || 0), 0);
  const counts = useMemo(() => {
    return {
      total: unified.length,
      external: unified.filter((r) => r.kind === "external").length,
      repairs: unified.filter((r) => r.kind === "internal_repair").length,
      services: unified.filter((r) => r.kind === "internal_service").length,
    };
  }, [unified]);

  const filteredRows = useMemo(() => {
    if (filter === "all") return unified;
    return unified.filter((r) => r.kind === filter);
  }, [unified, filter]);

  const submit = async () => {
    if (!form.service_name.trim()) return toast.error("Denumirea serviceului e obligatorie");
    if (!form.cost || parseFloat(form.cost) <= 0) return toast.error("Specifică costul");
    try {
      await api.post(`/cars/${carId}/external-repairs`, {
        ...form,
        cost: parseFloat(form.cost),
      });
      toast.success("Reparație externă înregistrată");
      setOpen(false);
      setForm({ date: new Date().toISOString().slice(0, 10), service_name: "", cost: "", currency: "CHF", description: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const remove = async (rid) => {
    if (!(await confirm({ body: "Ștergi această reparație externă?", danger: true }))) return;
    await api.delete(`/cars/${carId}/external-repairs/${rid}`);
    load();
  };

  // Mapping kind → afișaj
  const KIND_META = {
    external:         { icon: "🏭", label: "EXTERN",  badgeBg: "bg-amber-100 text-amber-900 border-amber-300" },
    internal_repair:  { icon: "🛠",  label: "INTERN · Reparație",  badgeBg: "bg-blue-100 text-blue-900 border-blue-300" },
    internal_service: { icon: "🔧", label: "INTERN · Revizie",  badgeBg: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  };

  return (
    <div className="card-flat p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-bold">📋 Istoric reparații și revizii</h3>
          <div className="text-xs text-zinc-500 mt-0.5">Toate intervențiile: interne (din magazia firmei) + externe (alt service)</div>
        </div>
        <div className="flex items-center gap-3">
          {totalExternalCost > 0 && (
            <div className="text-right">
              <div className="text-[10px] text-zinc-500 uppercase font-bold">Total extern</div>
              <div className="font-display text-lg font-extrabold text-amber-700">{totalExternalCost.toLocaleString("ro-RO")} CHF</div>
            </div>
          )}
          {canEdit && (
            <Button onClick={() => setOpen(true)} className="bg-[var(--primary)]" data-testid="ext-repair-add"><Plus size={14} className="mr-1"/>Adaugă reparație externă</Button>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { k: "all", label: `Toate (${counts.total})`, color: "bg-zinc-800 text-white" },
          { k: "internal_repair", label: `🛠 Reparații interne (${counts.repairs})`, color: "bg-blue-600 text-white" },
          { k: "internal_service", label: `🔧 Revizii (${counts.services})`, color: "bg-emerald-600 text-white" },
          { k: "external", label: `🏭 Externe (${counts.external})`, color: "bg-amber-600 text-white" },
        ].map((c) => (
          <button
            key={c.k}
            onClick={() => setFilter(c.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filter === c.k ? c.color + " shadow-md" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            data-testid={`repairs-filter-${c.k}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {externalRows.length > 0 && (
        <div className="mb-4">
          <ExternalRepairsMonthlyChart rows={externalRows} />
        </div>
      )}

      {filteredRows.length === 0 ? (
        <div className="text-sm text-zinc-400 italic text-center py-8" data-testid="repairs-history-empty">
          {filter === "all" ? "Nicio intervenție înregistrată pentru această mașină." : "Niciun rezultat pentru acest filtru."}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredRows.map((r) => {
            const meta = KIND_META[r.kind] || KIND_META.external;
            const isExternal = r.kind === "external";
            return (
              <div
                key={r.id || `${r.kind}-${r.ts}`}
                className="flex items-start gap-3 p-3 rounded border border-zinc-200 hover:border-zinc-300 bg-white"
                data-testid={`repair-history-${r.kind}-${r.id || r.ts}`}
              >
                <div className="text-2xl flex-shrink-0">{meta.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${meta.badgeBg}`}>{meta.label}</span>
                      <div className="font-semibold">{r.label}</div>
                      {r.defect_label && (
                        <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-900 border border-red-300" title={`Defect rezolvat: ${r.defect_label}`}>
                          ⚠ Defect rezolvat
                        </span>
                      )}
                    </div>
                    {r.cost != null && (
                      <div className="font-display font-extrabold text-amber-700">{(parseFloat(r.cost) || 0).toLocaleString("ro-RO")} {r.currency || "CHF"}</div>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">📅 {fmtRoDate(r.date)}{r.user ? ` · de ${r.user}` : ""}</div>
                  {r.defect_label && (
                    <div className="text-xs text-red-700 mt-1 font-medium">🔴 Defect raportat: <em>„{r.defect_label}"</em></div>
                  )}
                  {r.description && !isExternal && r.defect_label === null && (
                    <div className="text-xs text-zinc-600 mt-1 italic">{r.description}</div>
                  )}
                  {isExternal && r.description && (
                    <div className="text-sm text-zinc-700 mt-1.5">{r.description}</div>
                  )}
                  {(r.parts || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {r.parts.map((p, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-300 text-zinc-700">
                          {p.name} <strong>×{p.qty}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                  {r.oil_liters > 0 && (
                    <div className="mt-1 text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1">
                      🛢 {r.oil_liters} L ulei consumat
                    </div>
                  )}
                </div>
                {canEdit && isExternal && (
                  <button onClick={() => remove(r.id)} className="text-zinc-400 hover:text-red-600 p-1" title="Șterge">
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="ext-repair-dialog">
          <DialogHeader><DialogTitle>🏭 Adaugă reparație externă</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="small-label">Data</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm((x) => ({ ...x, date: e.target.value }))} className="mt-1"/>
              </div>
              <div>
                <Label className="small-label">Cost</Label>
                <Input type="number" step="0.01" min={0} value={form.cost} onChange={(e) => setForm((x) => ({ ...x, cost: e.target.value }))} className="mt-1" placeholder="0.00" data-testid="ext-repair-cost"/>
              </div>
            </div>
            <div>
              <Label className="small-label">Service / atelier *</Label>
              <Input value={form.service_name} onChange={(e) => setForm((x) => ({ ...x, service_name: e.target.value }))} className="mt-1" placeholder="ex: Garage Müller AG" data-testid="ext-repair-service"/>
            </div>
            <div>
              <Label className="small-label">Descriere reparație</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm((x) => ({ ...x, description: e.target.value }))} className="mt-1" placeholder="ex: schimbat pompă apă + curea distribuție"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={submit} className="bg-[var(--primary)]" data-testid="ext-repair-submit">Salvează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExternalRepairsMonthlyChart({ rows }) {
  // Group by YYYY-MM, sum cost
  const byMonth = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const ym = (r.date || "").slice(0, 7);
      if (!ym) continue;
      m[ym] = (m[ym] || 0) + (parseFloat(r.cost) || 0);
    }
    // Fill last 12 months
    const today = new Date();
    const list = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      list.push({ month: ym, label: d.toLocaleDateString("ro-RO", { month: "short", year: "2-digit" }), value: m[ym] || 0 });
    }
    return list;
  }, [rows]);
  const max = Math.max(1, ...byMonth.map((b) => b.value));
  const totalShown = byMonth.reduce((s, b) => s + b.value, 0);
  if (totalShown === 0) return null;
  return (
    <div className="mb-5 p-3 rounded-md bg-zinc-50 border border-zinc-200" data-testid="ext-repairs-chart">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-600">📈 Cheltuieli reparații — ultimele 12 luni</div>
        <div className="text-[10px] text-zinc-500">Total perioadă: <strong className="text-zinc-700">{totalShown.toLocaleString("ro-RO")} CHF</strong></div>
      </div>
      <div className="flex items-end gap-1 h-24">
        {byMonth.map((b) => (
          <div key={b.month} className="flex-1 flex flex-col items-center justify-end gap-1 group" title={`${b.label}: ${b.value.toLocaleString("ro-RO")} CHF`}>
            <div className="text-[9px] font-bold text-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {b.value > 0 ? `${b.value.toFixed(0)}` : ""}
            </div>
            <div
              className={`w-full rounded-t ${b.value > 0 ? "bg-emerald-500 group-hover:bg-emerald-600" : "bg-zinc-200"}`}
              style={{ height: `${Math.max(b.value > 0 ? 6 : 2, (b.value / max) * 70)}px` }}
            />
            <div className="text-[9px] text-zinc-500 truncate w-full text-center">{b.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DriverOnDatePane({ carId }) {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (d) => {
    if (!d) return;
    setLoading(true);
    try {
      const r = await api.get(`/cars/${carId}/driver-on-date`, { params: { date: d } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la căutare");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    search(today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId]);

  const sourceMeta = {
    calendar: { label: "Pontaj (override)", color: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: "✅", desc: "Cineva a raportat ore cu această mașină exact în această zi (sursă maximă de încredere)." },
    fuel:     { label: "Alimentare",         color: "bg-amber-100 text-amber-700 border-amber-300",       icon: "⛽", desc: "Persoana care a alimentat această mașină în ziua respectivă." },
    schedule: { label: "Planificat",          color: "bg-blue-100 text-blue-700 border-blue-300",          icon: "📅", desc: "Conform Planificator (atribuire săptămânală). Persoanele cu permis sunt afișate primele." },
    none:     { label: "Necunoscut",         color: "bg-zinc-100 text-zinc-600 border-zinc-300",          icon: "—", desc: "Nu există nicio sursă de date pentru această dată." },
  };
  const meta = data ? (sourceMeta[data.source] || sourceMeta.none) : null;

  return (
    <div className="card-flat p-6 mb-4" data-testid="driver-on-date-pane">
      <div className="mb-4">
        <h3 className="font-display text-lg font-bold">🔍 Cine a condus mașina într-o anumită zi?</h3>
        <div className="text-xs text-zinc-500 mt-0.5">Caută șoferul după prioritate: <strong>Pontaj override</strong> &gt; <strong>Alimentare</strong> &gt; <strong>Planificator</strong>.</div>
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs text-zinc-500">Dată</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="driver-on-date-input"/>
        </div>
        <Button onClick={() => search(date)} disabled={loading || !date} data-testid="driver-on-date-search-btn" className="bg-red-600 hover:bg-red-700 text-white">
          {loading ? "Se caută…" : "Caută"}
        </Button>
      </div>

      {data && (
        <div className="space-y-3" data-testid="driver-on-date-result">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] px-2 py-1 rounded border font-bold uppercase tracking-wider ${meta.color}`} data-testid="driver-source-badge">
              {meta.icon} {meta.label}
            </span>
            <span className="text-xs text-zinc-500">{meta.desc}</span>
          </div>

          {data.project && (
            <button
              type="button"
              onClick={() => data.project.project_id && navigate(`/proiecte?project=${data.project.project_id}&edit=1`)}
              className={`w-full flex items-center gap-2 p-2 rounded text-left text-sm border ${data.project.project_id ? "hover:bg-blue-50 border-blue-200" : "border-zinc-200 cursor-default"}`}
              data-testid="driver-on-date-project"
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: data.project.color || "#3B82F6" }}/>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Proiect:</span>
              <span className="font-semibold flex-1 truncate">{data.project.name || data.project.label || "(necunoscut)"}</span>
            </button>
          )}

          {data.drivers.length === 0 ? (
            <div className="text-sm text-zinc-400 italic text-center py-6 border border-dashed border-zinc-200 rounded">
              Nu există date despre cine a folosit mașina în {fmtRoDate(data.date)}.<br/>
              <span className="text-[11px]">Verifică în pontaj, alimentări sau planificator.</span>
            </div>
          ) : (
            <div className="space-y-2">
              {data.drivers.map((d, i) => (
                <div key={`${d.person_id || "x"}-${i}`} className="flex items-center gap-3 p-3 rounded border border-zinc-200" data-testid={`driver-on-date-row-${i}`}>
                  <div className="w-9 h-9 rounded-full grid place-items-center font-bold text-sm flex-shrink-0" style={{ background: d.color || "#E5E7EB", color: d.color ? "white" : "#71717A" }}>
                    {(d.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      {d.name}
                      {d.has_license && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider">🪪 Permis</span>}
                      {d.active === false && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600 font-bold">INACTIV</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-3 flex-wrap">
                      {d.telefon && <span>📞 {d.telefon}</span>}
                      {data.source === "calendar" && (
                        <>
                          {d.hours > 0 && <span>⏱ {d.hours}h lucrate</span>}
                          {d.hours_driven > 0 && <span>🚗 {d.hours_driven}h conduse</span>}
                          {d.note && <span className="italic">„{d.note}"</span>}
                        </>
                      )}
                      {data.source === "fuel" && (
                        <>
                          {d.fuel_liters !== undefined && d.fuel_liters !== null && <span>⛽ {d.fuel_liters} L</span>}
                          {d.fuel_km !== undefined && d.fuel_km !== null && <span>📏 {d.fuel_km} km</span>}
                          {d.fuel_chip && <span>🪪 chip {d.fuel_chip}</span>}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DailyDriverHistoryPane({ carId }) {
  const [rows, setRows] = useState(null);
  const [limit, setLimit] = useState(30);
  useEffect(() => {
    api.get(`/cars/${carId}/daily-driver-history`).then((r) => setRows(r.data?.rows || [])).catch(() => setRows([]));
  }, [carId]);
  if (rows === null) return <div className="card-flat p-4 mb-3 text-zinc-400 text-sm">Se încarcă istoric zilnic…</div>;
  if (rows.length === 0) return null;
  const visible = rows.slice(0, limit);
  return (
    <div className="card-flat p-6 mb-3" data-testid="daily-driver-history-pane">
      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold">📅 Istoric zilnic — șofer & șantier</h3>
          <div className="text-xs text-zinc-500 mt-0.5">Lista zi cu zi (cea mai recentă prima): cine a condus mașina și pe ce șantier.</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-[10px] uppercase tracking-wider text-zinc-600">
              <th className="text-left px-2 py-1.5">Dată</th>
              <th className="text-left px-2 py-1.5">Șofer</th>
              <th className="text-left px-2 py-1.5">Șantier / Regie</th>
              <th className="text-left px-2 py-1.5">Firmă</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => (
              <tr key={`${r.date}-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`daily-row-${i}`}>
                <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{fmtRoDate(r.date)}</td>
                <td className="px-2 py-1.5">
                  <span className="inline-flex items-center gap-1 font-medium">
                    <span className="text-blue-600">🚗</span> {r.driver_name || "(nealocat)"}
                    {r.drivers_in_team && r.drivers_in_team.length > 1 && (
                      <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-bold" title="Echipa avea mai mulți cu permis">+{r.drivers_in_team.length - 1}</span>
                    )}
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: r.color || "#3B82F6" }} />
                    <span className="truncate">{r.label || r.project_name || r.regie_name || "—"}</span>
                  </span>
                </td>
                <td className="px-2 py-1.5 text-xs text-zinc-600">{r.firma || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > limit && (
        <button
          type="button"
          className="mt-3 text-xs text-blue-600 hover:underline"
          onClick={() => setLimit((l) => l + 50)}
          data-testid="daily-history-load-more"
        >
          Arată încă 50 (mai sunt {rows.length - limit})
        </button>
      )}
    </div>
  );
}

function DriversHistoryPane({ carId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    api.get(`/cars/${carId}/drivers-history`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [carId]);

  if (rows === null) return <div className="card-flat p-6 text-zinc-400">Se încarcă…</div>;

  return (
    <div className="card-flat p-6">
      <div className="mb-4">
        <h3 className="font-display text-lg font-bold">👥 Istoric șoferi</h3>
        <div className="text-xs text-zinc-500 mt-0.5">Persoane care au folosit această mașină — agregat din planul săptămânal + orele raportate. Click pe un șofer pentru a vedea proiectele lui.</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-400 italic text-center py-8">Niciun șofer înregistrat. Apar aici după ce mașina e atribuită în Management și angajații raportează ore conduse.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => {
            const isOpen = !!expanded[r.person_id];
            const hasProjects = (r.projects || []).length > 0;
            return (
              <div key={r.person_id} className="rounded border border-zinc-200 overflow-hidden" data-testid={`driver-row-${i}`}>
                <button
                  type="button"
                  onClick={() => hasProjects && setExpanded((x) => ({ ...x, [r.person_id]: !x[r.person_id] }))}
                  className={`w-full flex items-center gap-3 p-3 text-left ${hasProjects ? "hover:bg-zinc-50 cursor-pointer" : "cursor-default"}`}
                >
                  <div className="w-7 h-7 rounded-full grid place-items-center font-bold text-sm flex-shrink-0" style={{ background: r.color || "#E5E7EB", color: r.color ? "white" : "#71717A" }}>
                    {(r.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold flex items-center gap-2">
                      {r.name}
                      {!r.active && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600 font-bold">INACTIV</span>}
                      {hasProjects && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">{r.projects.length} {r.projects.length === 1 ? "proiect" : "proiecte"}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {r.first_date && r.last_date && (r.first_date === r.last_date ? fmtRoDate(r.first_date) : `${fmtRoDate(r.first_date)} → ${fmtRoDate(r.last_date)}`)}
                      {r.telefon && <span className="ml-2">📞 {r.telefon}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-display text-base font-extrabold text-amber-600">{r.hours_driven_total} h</div>
                    <div className="text-[10px] text-zinc-500">{r.days_planned} {r.days_planned === 1 ? "zi" : "zile"} planificate</div>
                  </div>
                  {hasProjects && (
                    <span className={`text-zinc-400 transition-transform ${isOpen ? "rotate-90" : ""}`}>›</span>
                  )}
                </button>
                {isOpen && hasProjects && (
                  <div className="border-t border-zinc-100 bg-zinc-50/50 px-3 py-2 space-y-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Proiecte cu această mașină:</div>
                    {(r.projects || []).map((p) => (
                      <button
                        key={p.project_id}
                        type="button"
                        onClick={() => navigate(`/proiecte?project=${p.project_id}&edit=1`)}
                        className="w-full flex items-center gap-2 p-2 rounded hover:bg-blue-50 text-left text-sm"
                        data-testid={`driver-project-${p.project_id}`}
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color || "#3B82F6" }}/>
                        <span className="font-medium flex-1 truncate">{p.name}</span>
                        <span className="text-[10px] text-zinc-500">{fmtRoDate(p.last_date)}</span>
                        <span className="text-[10px] font-bold text-zinc-700 bg-zinc-100 px-1.5 py-0.5 rounded">{p.days} {p.days === 1 ? "zi" : "zile"}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProjectsHistoryPane({ carId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/cars/${carId}/projects-history`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [carId]);

  if (rows === null) return <div className="card-flat p-6 text-zinc-400">Se încarcă…</div>;

  return (
    <div className="card-flat p-6" data-testid="projects-history-pane">
      <div className="mb-4">
        <h3 className="font-display text-lg font-bold">📋 Istoric proiecte</h3>
        <div className="text-xs text-zinc-500 mt-0.5">Lista tuturor proiectelor la care a fost folosită mașina, cu durata și șoferii. Click pe un proiect pentru a-l deschide.</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-400 italic text-center py-8">Niciun proiect înregistrat. Apar aici după ce mașina e atribuită în Management la un proiect.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => (
            <button
              key={p.project_id}
              type="button"
              onClick={() => navigate(`/proiecte?project=${p.project_id}&edit=1`)}
              className="w-full flex items-start gap-3 p-3 rounded border border-zinc-200 hover:border-blue-400 hover:bg-blue-50/40 text-left"
              data-testid={`car-project-row-${p.project_id}`}
            >
              <span
                className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                style={{ background: p.color || "#3B82F6" }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold truncate">{p.name}</span>
                  {p.category && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 font-bold uppercase">cat. {p.category}</span>}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 flex flex-wrap gap-x-3">
                  <span>📅 {fmtRoDate(p.first_date)} → {fmtRoDate(p.last_date)}</span>
                  {p.adresa && <span className="truncate max-w-[280px]">📍 {p.adresa}</span>}
                  {p.auftraggeber && <span>👔 {p.auftraggeber}</span>}
                </div>
                {(p.drivers || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {p.drivers.map((d) => (
                      <span
                        key={d.person_id}
                        className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded"
                        style={{
                          background: d.color ? `${d.color}22` : "#F3F4F6",
                          color: d.color || "#52525B",
                        }}
                      >
                        🧑 {d.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-display text-base font-extrabold text-blue-700">{p.total_days}</div>
                <div className="text-[10px] text-zinc-500 uppercase">{p.total_days === 1 ? "zi" : "zile"}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// Lista comună orașe Elveția (cele mai folosite pentru permise de parcare)
const COMMON_CITIES = [
  { code: "ZH", name: "Zürich" },
  { code: "BE", name: "Bern" },
  { code: "BS", name: "Basel" },
  { code: "GE", name: "Genève" },
  { code: "LU", name: "Luzern" },
  { code: "SG", name: "St. Gallen" },
  { code: "WI", name: "Winterthur" },
  { code: "ZG", name: "Zug" },
  { code: "FR", name: "Fribourg" },
  { code: "TG", name: "Thurgau" },
  { code: "AG", name: "Aargau" },
  { code: "SO", name: "Solothurn" },
  { code: "SH", name: "Schaffhausen" },
];

function ParkingPermitsPane({ permits, canEdit, onChange }) {
  const [city, setCity] = useState("ZH");
  const [zone, setZone] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [note, setNote] = useState("");

  const today = new Date().toISOString().slice(0, 10);
  const sortedPermits = [...permits].sort((a, b) => (a.valid_until || "").localeCompare(b.valid_until || ""));

  const addPermit = () => {
    if (!city) { toast.error("Selectează orașul"); return; }
    const found = COMMON_CITIES.find((c) => c.code === city);
    const newPermit = {
      id: `pp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      city_code: city,
      city_name: found?.name || city,
      zone: zone.trim() || null,
      valid_until: validUntil || null,
      note: note.trim() || null,
      created_at: new Date().toISOString(),
    };
    onChange([...permits, newPermit]);
    setZone(""); setValidUntil(""); setNote("");
    toast.success("Permis adăugat");
  };

  const removePermit = (pid) => {
    onChange(permits.filter((p) => p.id !== pid));
    toast.success("Permis șters");
  };

  return (
    <div className="card-flat p-6">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-display font-bold text-lg">🅿️ Permise de parcare</h3>
        <span className="text-xs text-zinc-500">({permits.length} {permits.length === 1 ? "permis" : "permise"})</span>
      </div>

      {sortedPermits.length === 0 ? (
        <div className="text-sm text-zinc-400 italic py-4 text-center border-2 border-dashed border-zinc-200 rounded-md">
          Niciun permis de parcare înregistrat.
        </div>
      ) : (
        <div className="space-y-2 mb-6">
          {sortedPermits.map((p) => {
            const expired = p.valid_until && p.valid_until < today;
            const expiringSoon = p.valid_until && !expired && p.valid_until <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
            const stateClass = expired ? "bg-red-50 border-red-300" : expiringSoon ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-200";
            return (
              <div
                key={p.id}
                className={`flex items-center gap-3 p-3 rounded-md border-2 ${stateClass}`}
                data-testid={`permit-row-${p.id}`}
              >
                <span className="text-lg font-extrabold tracking-wider px-2 py-0.5 rounded bg-white border border-zinc-300">
                  {p.city_code}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">
                    {p.city_name || p.city_code}
                    {p.zone && <span className="ml-2 text-xs font-normal text-zinc-600">· {p.zone}</span>}
                  </div>
                  <div className="text-xs text-zinc-600 mt-0.5">
                    {p.valid_until ? (
                      <>
                        Valabil până la <strong>{fmtRoDate(p.valid_until)}</strong>
                        {expired && <span className="ml-2 text-red-700 font-bold uppercase">EXPIRAT</span>}
                        {expiringSoon && <span className="ml-2 text-amber-700 font-bold uppercase">expiră curând</span>}
                      </>
                    ) : (
                      <span className="italic text-zinc-400">fără dată de expirare</span>
                    )}
                  </div>
                  {p.note && <div className="text-[11px] text-zinc-500 mt-0.5 italic">{p.note}</div>}
                </div>
                {canEdit && (
                  <button
                    onClick={() => removePermit(p.id)}
                    className="text-zinc-400 hover:text-red-600 p-1.5"
                    title="Șterge permisul"
                    data-testid={`del-permit-${p.id}`}
                  >
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && (
        <div className="border-t pt-4 space-y-3">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-600">+ Adaugă permis nou</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="small-label">Oraș</Label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger className="mt-1" data-testid="permit-city"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {COMMON_CITIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.code} · {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Zonă (opțional)</Label>
              <Input
                value={zone}
                onChange={(e) => setZone(e.target.value)}
                placeholder="ex: zona albastră"
                className="mt-1"
                data-testid="permit-zone"
              />
            </div>
            <div>
              <Label className="small-label">Valabil până la</Label>
              <Input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                className="mt-1"
                data-testid="permit-valid-until"
              />
            </div>
            <div>
              <Label className="small-label">Notă (opțional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="ex: nr permis, condiții"
                className="mt-1"
                data-testid="permit-note"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={addPermit} data-testid="add-permit-btn" className="bg-[var(--primary)] hover:opacity-90">
              + Adaugă permis
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function EditDefectDialog({ defect, carLabel, carId, onClose, onSaved, onPreview }) {
  const [reason, setReason] = useState(defect.reason || "");
  const [severity, setSeverity] = useState(defect.severity || "medium");
  const [drivable, setDrivable] = useState(!!defect.drivable);
  const [imageIds, setImageIds] = useState(defect.image_ids || []);
  const [saving, setSaving] = useState(false);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      await onSaved({ reason: reason.trim() || defect.reason, severity, drivable, image_ids: imageIds, ...extra });
    } finally {
      setSaving(false);
    }
  };

  const fixWithPart = async () => {
    const sp = defect.spare_part_name;
    const inv_id = defect.spare_part_inventory_id;
    let decrement = false;
    if (sp && inv_id) {
      const ok = window.confirm(`Marchezi defectul ca REPARAT.\n\nFolosesc piesa „${sp}" din stoc?\n• OK = scad o bucată din inventar\n• Anulează = doar marchez reparat (fără scăderea inventarului)`);
      decrement = !!ok;
    } else if (!window.confirm("Marchezi defectul ca REPARAT?")) {
      return;
    }
    setSaving(true);
    try {
      await api.post(`/cars/${carId}/defects/${defect.id}/fix`, { decrement_inventory: decrement });
      toast.success(decrement ? "✓ Defect reparat — piesa scăzută din stoc" : "✓ Defect marcat reparat");
      onClose();
      onSaved && onSaved.refresh && onSaved.refresh();
      // Trigger a re-fetch in parent via a synthetic save call (without changes)
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la reparație");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">⚠ Modifică / Repară defect</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-500">
            {carLabel} · raportat de <strong>{defect.reported_by || "—"}</strong> la {defect.reported_at ? fmtRoDate(defect.reported_at) : "—"}
          </div>
          {defect.spare_part_name && (
            <div className={`rounded-md p-2 border-l-4 ${
              defect.spare_part_status === "in_stock" ? "bg-emerald-50 border-emerald-500 text-emerald-900" :
              defect.spare_part_status === "ordered" ? "bg-amber-50 border-amber-500 text-amber-900" :
              "bg-red-50 border-red-500 text-red-900"
            }`}>
              <div className="text-xs font-semibold uppercase tracking-wider mb-0.5">🔩 Piesă necesară</div>
              <div className="text-sm font-bold">{defect.spare_part_name}</div>
              <div className="text-[11px] mt-1">
                Status: {
                  defect.spare_part_status === "in_stock" ? "✓ În stoc — gata de folosit" :
                  defect.spare_part_status === "ordered" ? "📦 Comandată — așteaptă livrare" :
                  defect.spare_part_status === "used" ? "✓ Folosită" :
                  "⚠ De comandat"
                }
              </div>
            </div>
          )}
          <div>
            <Label className="small-label">Descriere defect</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="edit-defect-reason"
            />
          </div>
          <div>
            <Label className="small-label">Gravitate</Label>
            <div className="flex gap-2 mt-1">
              {[
                { v: "low", label: "Mică", color: "bg-yellow-400 hover:bg-yellow-500", text: "text-zinc-900" },
                { v: "medium", label: "Medie", color: "bg-orange-500 hover:bg-orange-600", text: "text-white" },
                { v: "high", label: "Mare", color: "bg-red-600 hover:bg-red-700", text: "text-white" },
              ].map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setSeverity(s.v)}
                  className={`flex-1 rounded-md py-2 text-xs font-bold transition-all ${s.color} ${s.text} ${severity === s.v ? "ring-2 ring-offset-2 ring-zinc-900 scale-105" : "opacity-70 hover:opacity-100"}`}
                  data-testid={`edit-severity-${s.v}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="small-label">Mai poate fi condusă?</Label>
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => setDrivable(true)}
                className={`flex-1 rounded-md py-2 text-xs font-bold transition-all border-2 ${drivable ? "bg-emerald-100 border-emerald-600 text-emerald-900 ring-2 ring-emerald-400" : "bg-white border-zinc-200 text-zinc-500 hover:border-emerald-300"}`}
                data-testid="edit-drivable-yes"
              >
                ✓ DA — poate circula
              </button>
              <button
                type="button"
                onClick={() => setDrivable(false)}
                className={`flex-1 rounded-md py-2 text-xs font-bold transition-all border-2 ${!drivable ? "bg-red-100 border-red-600 text-red-900 ring-2 ring-red-400" : "bg-white border-zinc-200 text-zinc-500 hover:border-red-300"}`}
                data-testid="edit-drivable-no"
              >
                ✗ NU — imobilizată
              </button>
            </div>
          </div>
          <div className="border-t border-zinc-200 pt-3">
            <Label className="small-label">📷 Poze defect</Label>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <UploadButton
                testId="edit-defect-image-upload"
                label={imageIds.length > 0 ? `+ Mai adaugă (${imageIds.length})` : "📷 Adaugă poză"}
                accept="image/*"
                onUploaded={(f) => setImageIds((prev) => [...prev, f.id])}
              />
              {imageIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setImageIds([])}
                  className="text-[11px] text-red-600 hover:underline"
                  data-testid="edit-defect-images-clear"
                >
                  🗑 Șterge toate
                </button>
              )}
            </div>
            {imageIds.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-2" data-testid="edit-defect-images-preview">
                {imageIds.map((fid, idx) => (
                  <div key={fid} className="relative group rounded overflow-hidden border border-zinc-200 aspect-square bg-zinc-50">
                    <button
                      type="button"
                      onClick={() => onPreview && onPreview({ fileId: fid, name: `defect-${defect.id}-${idx + 1}` })}
                      className="w-full h-full block"
                      title="Click pentru preview"
                    >
                      <AuthImg fileId={fid} className="w-full h-full object-cover" alt={`Poză defect ${idx + 1}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageIds((prev) => prev.filter((x) => x !== fid))}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Șterge"
                      data-testid={`edit-defect-img-remove-${idx}`}
                    >
                      ✗
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 flex-wrap mt-4">
          <Button
            variant="outline"
            onClick={fixWithPart}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 font-bold"
            data-testid="defect-mark-fixed"
            title={defect.spare_part_name ? "Marchează rezolvat (cu confirmare scădere stoc)" : "Marchează defectul ca reparat"}
          >
            ✓ Repară (marchează rezolvat)
          </Button>
          <div className="flex-1"/>
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={() => save()} disabled={saving} className="bg-[var(--primary)] hover:opacity-90" data-testid="defect-save">
            Salvează modificări
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* =====================================================================
   CarToolSetView — afișează sculele din setul de culoare + echipa pe ziua curentă
===================================================================== */
function CarToolSetView({ carId, color }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = React.useState(today);
  const [data, setData] = React.useState({ items: [], team: [], location: null });
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!carId || !color) return;
    setLoading(true);
    api.get(`/cars/${carId}/tool-set?date=${date}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], team: [], location: null }))
      .finally(() => setLoading(false));
  }, [carId, color, date]);

  const COLOR_META = {
    rosu: { label: "Roșu", emoji: "🔴", bg: "bg-red-50", border: "border-red-200" },
    galben: { label: "Galben", emoji: "🟡", bg: "bg-yellow-50", border: "border-yellow-200" },
    albastru: { label: "Albastru", emoji: "🔵", bg: "bg-blue-50", border: "border-blue-200" },
    verde: { label: "Verde", emoji: "🟢", bg: "bg-emerald-50", border: "border-emerald-200" },
    gri: { label: "Gri", emoji: "⚪", bg: "bg-zinc-100", border: "border-zinc-300" },
    mov: { label: "Mov", emoji: "🟣", bg: "bg-purple-50", border: "border-purple-200" },
  };
  const meta = COLOR_META[color] || { label: color, emoji: "🎨", bg: "bg-zinc-50", border: "border-zinc-200" };

  return (
    <div className={`mt-4 rounded-lg border ${meta.border} ${meta.bg} p-3`} data-testid="car-tool-set-view">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-sm font-bold text-zinc-800">
          {meta.emoji} Set {meta.label} <span className="text-zinc-500 font-normal">· locația virtuală: <span className="font-mono">{data.location || "—"}</span></span>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-zinc-600">Echipa pe:</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-[150px] text-xs" data-testid="car-tool-set-date"/>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Scule */}
        <div className="rounded border bg-white p-2.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-600 mb-2">
            🛠 Scule / echipament ({data.items?.length || 0})
          </div>
          {loading && <div className="text-xs text-zinc-400 italic">Se încarcă...</div>}
          {!loading && (!data.items || data.items.length === 0) && (
            <div className="text-xs text-zinc-400 italic">
              Nicio sculă în setul {meta.label}. Mută scule din Inventar la locația „{data.location || `SET ${meta.label.toUpperCase()}`}".
            </div>
          )}
          {!loading && data.items && data.items.length > 0 && (
            <ul className="space-y-1 max-h-[200px] overflow-auto">
              {data.items.map((it) => (
                <li key={it.id} className="flex items-center justify-between text-xs py-1 border-b border-zinc-100 last:border-0" data-testid={`car-tool-item-${it.id}`}>
                  <span className="font-semibold text-zinc-800">{it.name}</span>
                  <span className="text-zinc-500">{it.quantity || 1} {it.unit || ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Echipa */}
        <div className="rounded border bg-white p-2.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-600 mb-2">
            👥 Echipa pe {date} ({data.team?.length || 0})
          </div>
          {loading && <div className="text-xs text-zinc-400 italic">Se încarcă...</div>}
          {!loading && (!data.team || data.team.length === 0) && (
            <div className="text-xs text-zinc-400 italic">
              Nicio persoană alocată pe mașină pe această zi (Planificator).
            </div>
          )}
          {!loading && data.team && data.team.length > 0 && (
            <ul className="space-y-1 max-h-[200px] overflow-auto">
              {data.team.map((p) => (
                <li key={p.person_id} className="flex items-center justify-between text-xs py-1 border-b border-zinc-100 last:border-0" data-testid={`car-team-${p.person_id}`}>
                  <span className="font-semibold text-zinc-800">{p.person_name}</span>
                  <span className="text-[10px] text-zinc-500 italic">{p.context}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}


// ============== RecentRepairsCard ==============
// Card prominent afișat la începutul paginii unui autoturism cu ultimele 3 reparații/revizii.
function RecentRepairsCard({ carId, onSeeAll }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/cars/${carId}/repairs-history`)
      .then((r) => { if (!cancel) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancel) setRows([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [carId]);

  const KIND_META = {
    external:         { icon: "🏭", label: "Service extern",  color: "#b45309", bg: "#fef3c7" },
    internal_repair:  { icon: "🛠",  label: "Reparație internă", color: "#1d4ed8", bg: "#dbeafe" },
    internal_service: { icon: "🔧", label: "Revizie internă",   color: "#15803d", bg: "#dcfce7" },
  };

  const recent = rows.slice(0, 3);
  if (loading) return null;
  return (
    <div className="card-flat p-4 mb-3" data-testid="car-recent-repairs">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#FFEDD5] text-[#EA580C] grid place-items-center">
            <span className="text-lg">🔧</span>
          </div>
          <div>
            <div className="font-display font-bold text-base">Reparații &amp; revizii</div>
            <div className="text-[11px] text-zinc-500">
              {rows.length === 0 ? "Nicio intervenție înregistrată" :
                `${rows.length} intervenție${rows.length === 1 ? "" : "i"} · ultima ${fmtRoDate((rows[0] || {}).date)}`}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onSeeAll} data-testid="car-recent-repairs-see-all" className="gap-1">
          Vezi toate <ChevronRight size={14}/>
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-400 italic px-1">
          Apasă pe tabul &bdquo;📋 Istoric reparații și revizii&rdquo; pentru a adăuga prima intervenție externă, sau folosește &bdquo;🔧 Piese &amp; Consumabile&rdquo; pentru reparații interne.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {recent.map((r) => {
            const meta = KIND_META[r.kind] || KIND_META.external;
            return (
              <li
                key={r.id || `${r.kind}-${r.ts}`}
                className="flex items-start gap-3 p-2.5 rounded border border-zinc-200 hover:border-zinc-300 bg-white cursor-pointer transition-colors"
                onClick={onSeeAll}
                data-testid={`car-recent-repair-${r.id || r.ts}`}
              >
                <div className="w-9 h-9 rounded grid place-items-center flex-shrink-0 text-lg" style={{ background: meta.bg, color: meta.color }}>
                  {meta.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                    <span className="text-xs font-bold text-zinc-700">📅 {fmtRoDate(r.date)}</span>
                    {r.user && <span className="text-[11px] text-zinc-600">· 👤 <strong>{r.user}</strong></span>}
                    {r.cost != null && (
                      <span className="ml-auto font-display font-bold text-amber-700">{(parseFloat(r.cost) || 0).toLocaleString("ro-RO")} {r.currency || "CHF"}</span>
                    )}
                  </div>
                  <div className="text-[12px] text-zinc-700 mt-0.5 line-clamp-2">
                    {r.label || r.description || "—"}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

